// Decision Room — section editors (seller side, V1).
//
// Five editors live in this file so they share the same minimal
// styling and `useReload` pattern without each pulling its own
// dependency tree. Each editor expects:
//   - data: the full /api/buyer-spaces/:id payload
//   - authToken, spaceId: identity + target
//   - onReload: re-fetch the Space so post-save state reflects truth
//
// Pattern: optimistic UI is intentionally avoided here — every save
// re-fetches the Space so the seller sees server-validated state.
// That's fine because all of these endpoints are sub-100ms.

import { useEffect, useRef, useState } from 'react';
import MentionInput, { filterMentionsAgainstBody, filterAssetRefsAgainstBody, renderBodyWithMentions, roomAssetsFromData } from './MentionInput';
// Shared with the prospect's document cards and the standalone reading
// page. Importing the HELPERS (not the lazy viewer) is deliberate —
// see the file header in docDisplay.js for why they live there.
import {
  fileKind,
  formatBytes,
  formatPageCount,
} from '../utils/docDisplay';
import {
  internalDocBase,
  internalShareUrl,
  resolveInternalShareToken,
  selfPreviewLabel,
} from '../utils/internalShare';

const API_BASE = process.env.REACT_APP_API_URL || 'https://trailercst-backend-production.up.railway.app';

// ─── Shared primitives ──────────────────────────────────────────

// Surface tokens map to App.css --room-* CSS variables that flip
// based on data-theme. Editors here render inside BuyerSpaceDetail
// which now adapts to the seller's chosen app theme.
const cardStyle = {
  background: 'var(--room-card)',
  border: '1px solid var(--room-border)',
  borderRadius: 12,
  padding: 24,
  color: 'var(--room-text-strong)',
};
const inputStyle = {
  width: '100%', padding: '10px 12px',
  border: '1px solid var(--room-border)',
  background: 'var(--room-card)',
  color: 'var(--room-text-strong)',
  borderRadius: 8, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box',
};
const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: '0.05em',
  color: 'var(--room-text-muted)', marginBottom: 6,
};
// Primary action button — gradient purple/blue matches the rest of
// the app's brand (mirrors --gradient-main from App.css). Used on
// "Save", "Add", "Post", etc. Disabled state dims to 0.5 opacity
// without losing the gradient so you can still tell it's THIS
// button when shown alongside secondaries.
const primaryBtnStyle = {
  padding: '10px 18px', borderRadius: 10, border: 'none',
  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
  color: '#fff', cursor: 'pointer',
  fontSize: 14, fontWeight: 600,
  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)',
  transition: 'transform 0.12s ease, box-shadow 0.12s ease, opacity 0.12s ease',
  fontFamily: 'inherit',
};
const secondaryBtnStyle = {
  padding: '10px 16px', borderRadius: 10,
  border: '1px solid var(--room-border)',
  background: 'var(--room-card)',
  color: 'var(--room-text-body)',
  cursor: 'pointer',
  fontSize: 14, fontWeight: 500,
  transition: 'background 0.12s ease, border-color 0.12s ease',
  fontFamily: 'inherit',
};
// Delete button — outlined red pill that hovers to a tinted red
// fill. Replaces the bare-link treatment so destructive actions
// read as deliberate, not as an afterthought.
const dangerLinkStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  background: 'var(--room-card)',
  border: '1px solid rgba(239, 68, 68, 0.3)',
  color: '#dc2626',
  cursor: 'pointer',
  fontSize: 13, fontWeight: 600,
  padding: '8px 14px',
  borderRadius: 10,
  transition: 'background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
  fontFamily: 'inherit',
};

// Subtle ghost-toggle for per-row visibility. Filled-eye = visible
// to prospect; slashed-eye = hidden. Clicking flips state.
const visibilityToggleStyle = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 36, height: 36,
  background: 'var(--room-card)',
  border: '1px solid var(--room-border)',
  borderRadius: 10,
  cursor: 'pointer',
  transition: 'background 0.12s ease, border-color 0.12s ease, color 0.12s ease',
  fontFamily: 'inherit',
};

// "Revise with AI" button — distinct visual so sellers spot it
// alongside a save action. Smaller than the primary, sparkle icon.
const aiBtnStyle = {
  padding: '6px 12px', borderRadius: 8, border: '1px solid #c4b5fd',
  background: 'rgba(168, 85, 247, 0.08)', color: '#7c3aed',
  cursor: 'pointer', fontSize: 13, fontWeight: 600,
  display: 'inline-flex', alignItems: 'center', gap: 6,
  transition: 'background 0.12s ease, border-color 0.12s ease, opacity 0.12s ease',
  fontFamily: 'inherit',
};

// Tiny sparkle SVG so the AI button reads as "AI" without leaning
// on emoji (per design pass — emojis got removed from the sidebar
// for the same reason).
function SparkleIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2l1.5 5.5L19 9l-5.5 1.5L12 16l-1.5-5.5L5 9l5.5-1.5L12 2zM19 14l.75 2.75L22.5 17.5l-2.75.75L19 21l-.75-2.75L15.5 17.5l2.75-.75L19 14z" />
    </svg>
  );
}

// ReviseWithAI — POSTs current text + fieldType to /api/ai/revise,
// stuffs the revised text back via setValue, and exposes Undo for
// the next ~30s in case the seller doesn't like the result. Designed
// to live next to the Save button so it reads as a sibling action.
export function ReviseWithAI({ value, setValue, authToken, fieldType, label = 'Revise with AI', disabled = false }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [previousValue, setPreviousValue] = useState(null);

  const revise = async () => {
    if (!value || !value.trim()) {
      setError('Add some text first — AI revises what you wrote.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/ai/revise`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text: value, fieldType }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.error || `Revision failed (${r.status})`);
      }
      const body = await r.json();
      if (!body.revisedText) throw new Error('Empty revision');
      setPreviousValue(value);
      setValue(body.revisedText);
      // Auto-clear undo state after 30s — long enough for a quick
      // reaction, short enough that the button doesn't permanently
      // sit in undo mode.
      setTimeout(() => setPreviousValue(null), 30000);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const undo = () => {
    if (previousValue == null) return;
    setValue(previousValue);
    setPreviousValue(null);
  };

  if (previousValue != null) {
    return (
      <button
        type="button"
        style={{ ...secondaryBtnStyle, fontSize: 13 }}
        onClick={undo}
        title="Restore your original text"
      >
        ↶ Undo AI revision
      </button>
    );
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <button
        type="button"
        style={{ ...aiBtnStyle, opacity: (busy || disabled) ? 0.6 : 1, cursor: (busy || disabled) ? 'wait' : 'pointer' }}
        onClick={revise}
        disabled={busy || disabled}
        title="Let AI rewrite this text — undo within 30s if you don't like the result"
      >
        <SparkleIcon />
        {busy ? 'Revising…' : label}
      </button>
      {error && <span style={{ color: '#dc2626', fontSize: 12 }}>{error}</span>}
    </span>
  );
}

function authHeaders(authToken, withJson = true) {
  const h = { Authorization: `Bearer ${authToken}` };
  if (withJson) h['Content-Type'] = 'application/json';
  return h;
}

// PATCH the Space row with the given partial. Centralized so every
// "free-text field" editor uses the same code path.
async function patchSpace(spaceId, authToken, body) {
  const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}`, {
    method: 'PATCH',
    headers: authHeaders(authToken),
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || `Save failed (status ${r.status})`);
  }
  return r.json();
}

// ─── Welcome editor ─────────────────────────────────────────────

export function WelcomeEditor({ data, authToken, spaceId, onReload }) {
  const space = data.buyerSpace;
  const [message, setMessage] = useState(space.welcomeMessage || '');
  const [videoUrl, setVideoUrl] = useState(space.welcomeVideoUrl || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const dirty = message !== (space.welcomeMessage || '') || videoUrl !== (space.welcomeVideoUrl || '');

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await patchSpace(spaceId, authToken, {
        welcomeMessage: message,
        welcomeVideoUrl: videoUrl,
      });
      onReload();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={cardStyle}>
      <p style={{ marginTop: 0, color: 'var(--room-text-muted)', fontSize: 14 }}>
        First thing the prospect sees when they open the Decision Room. Keep it short,
        warm, and personal — bonus points for a 30-second video greeting.
      </p>
      <div style={{ marginTop: 20 }}>
        <label style={labelStyle} htmlFor="welcome-message">Welcome message</label>
        <textarea
          id="welcome-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Hi Sarah — thanks for taking the time. I've put together everything we discussed in one place..."
          rows={6}
          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
        />
      </div>
      <div style={{ marginTop: 20 }}>
        <label style={labelStyle} htmlFor="welcome-video">Intro video URL (optional)</label>
        <input
          id="welcome-video"
          type="url"
          value={videoUrl}
          onChange={(e) => setVideoUrl(e.target.value)}
          placeholder="https://www.loom.com/share/..."
          style={inputStyle}
        />
        <p style={{ fontSize: 12, color: 'var(--room-text-subtle)', marginTop: 6 }}>
          Loom, Vidyard, or any direct .mp4 URL. Renders inline above the welcome message on the prospect view.
        </p>
      </div>
      {error && <div style={{ color: '#dc2626', marginTop: 12, fontSize: 13 }}>{error}</div>}
      <div style={{ marginTop: 20, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" style={primaryBtnStyle} onClick={save} disabled={!dirty || saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <ReviseWithAI
          value={message}
          setValue={setMessage}
          authToken={authToken}
          fieldType="welcome"
        />
        {dirty && (
          <button
            type="button"
            style={secondaryBtnStyle}
            onClick={() => {
              setMessage(space.welcomeMessage || '');
              setVideoUrl(space.welcomeVideoUrl || '');
            }}
          >
            Revert
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Why We're Here editor ──────────────────────────────────────

export function DiscoveryEditor({ data, authToken, spaceId, onReload }) {
  const space = data.buyerSpace;
  const [summary, setSummary] = useState(space.discoverySummary || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const dirty = summary !== (space.discoverySummary || '');

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await patchSpace(spaceId, authToken, { discoverySummary: summary });
      onReload();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={cardStyle}>
      <p style={{ marginTop: 0, color: 'var(--room-text-muted)', fontSize: 14 }}>
        Capture the prospect's pain, current state, and success criteria so every
        stakeholder lands on the same understanding of why we're talking. Use sections
        like <code>**Pain points**</code>, <code>**Current state**</code>, <code>**Success criteria**</code>.
      </p>
      <div style={{ marginTop: 20 }}>
        <label style={labelStyle} htmlFor="discovery">Discovery summary</label>
        <textarea
          id="discovery"
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          placeholder="**Pain points**&#10;- Spends 6 hours every Friday reconciling spreadsheets&#10;- Macros break monthly, requires IT to fix&#10;&#10;**Success criteria**&#10;- Real-time reports without remote desktop..."
          rows={14}
          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }}
        />
      </div>
      {error && <div style={{ color: '#dc2626', marginTop: 12, fontSize: 13 }}>{error}</div>}
      <div style={{ marginTop: 20, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" style={primaryBtnStyle} onClick={save} disabled={!dirty || saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <ReviseWithAI
          value={summary}
          setValue={setSummary}
          authToken={authToken}
          fieldType="discovery"
        />
        {dirty && (
          <button
            type="button"
            style={secondaryBtnStyle}
            onClick={() => setSummary(space.discoverySummary || '')}
          >
            Revert
          </button>
        )}
      </div>
    </div>
  );
}

// ─── MAP editor ─────────────────────────────────────────────────

export function MapEditor({ data, authToken, spaceId, onReload, autoExpandActionId, onAutoExpandConsumed }) {
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDueDate, setNewDueDate] = useState('');
  const [newOwnerSide, setNewOwnerSide] = useState('seller');
  const [newIsInternal, setNewIsInternal] = useState(false);
  const [openComments, setOpenComments] = useState({});      // { actionId: bool }
  // MAP templates (2026-08-24).
  const [mapTemplates, setMapTemplates] = useState([]);
  const [pickedTemplate, setPickedTemplate] = useState('');
  const [applying, setApplying] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);

  const loadMapTemplates = async () => {
    try {
      const r = await fetch(`${API_BASE}/api/map-templates`, { headers: authHeaders(authToken) });
      if (!r.ok) throw new Error(`status ${r.status}`);
      const j = await r.json();
      setMapTemplates(Array.isArray(j.templates) ? j.templates : []);
    } catch (err) {
      // A template picker that fails to load must not break the plan
      // editor underneath it. Empty list hides the whole card.
      console.error('[map-templates] load failed:', err);
      setMapTemplates([]);
    }
  };

  useEffect(() => {
    if (!authToken) return;
    loadMapTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  const applyTemplate = async () => {
    if (!pickedTemplate || applying) return;
    setApplying(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/actions/apply-template`, {
        method: 'POST',
        headers: authHeaders(authToken),
        body: JSON.stringify({ templateId: pickedTemplate }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `Apply failed (status ${r.status})`);
      setPickedTemplate('');
      onReload();
    } catch (err) {
      setError(err.message);
    } finally {
      setApplying(false);
    }
  };

  const saveAsTemplate = async () => {
    if (savingTemplate) return;
    const name = window.prompt('Name this template');
    if (name === null) return;
    if (!String(name).trim()) return;
    setSavingTemplate(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/map-templates`, {
        method: 'POST',
        headers: authHeaders(authToken),
        body: JSON.stringify({ name: String(name).trim(), fromBuyerSpaceId: spaceId }),
      });
      const j = await r.json().catch(() => ({}));
      // 409 is the useful one: a name clash is a real answer, not a
      // failure, and saying "already exists" beats "Save failed".
      if (!r.ok) throw new Error(j.error || `Save failed (status ${r.status})`);
      await loadMapTemplates();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingTemplate(false);
    }
  };

  // Deep-link from the Conversations inbox: when the seller clicks
  // "Open conversation" on a MAP-comment row, we land here with
  // autoExpandActionId set. Auto-expand the matching action's
  // comment thread and scroll it into view, then tell the parent
  // we've consumed the deep-link so they drop it from the URL hash.
  useEffect(() => {
    if (!autoExpandActionId) return;
    if (!Array.isArray(data?.actions)) return;
    const target = data.actions.find((a) => a.id === autoExpandActionId);
    if (!target) {
      // Action doesn't exist (deleted?) — clear the pending hash so
      // the seller doesn't get stuck looking for nothing.
      if (onAutoExpandConsumed) onAutoExpandConsumed();
      return;
    }
    setOpenComments((prev) => ({ ...prev, [target.id]: true }));
    // setTimeout so the DOM has actually rendered the expanded
    // section before we try to scroll into it.
    setTimeout(() => {
      const el = document.getElementById(`map-action-${target.id}`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      if (onAutoExpandConsumed) onAutoExpandConsumed();
    }, 60);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoExpandActionId, data && data.actions && data.actions.length]);

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/actions`, {
        method: 'POST',
        headers: authHeaders(authToken),
        body: JSON.stringify({
          title: newTitle.trim(),
          dueDate: newDueDate || null,
          ownerSide: newOwnerSide,
          isInternal: newIsInternal,
          ordinal: data.actions.length,
        }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.error || `Create failed (status ${r.status})`);
      }
      setNewTitle('');
      setNewDueDate('');
      setNewOwnerSide('seller');
      setNewIsInternal(false);
      onReload();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const updateAction = async (actionId, patch) => {
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/actions/${actionId}`, {
        method: 'PATCH',
        headers: authHeaders(authToken),
        body: JSON.stringify(patch),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.error || `Update failed (status ${r.status})`);
      }
      onReload();
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteAction = async (actionId) => {
    if (!window.confirm('Delete this action item? This cannot be undone.')) return;
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/actions/${actionId}`, {
        method: 'DELETE',
        headers: authHeaders(authToken),
      });
      if (!r.ok) throw new Error(`Delete failed (status ${r.status})`);
      onReload();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div>
      {/* Templates (2026-08-24). Above the add-item card because
          starting from a template is the first decision, not an
          afterthought — a rep who has typed three items by hand is not
          going to stop and apply one. Hidden entirely when the
          workspace has no templates and the room has no plan to save,
          so an empty workspace isn't shown machinery for content that
          doesn't exist yet. */}
      {(mapTemplates.length > 0 || (data.actions && data.actions.length > 0)) && (
        <div style={{ ...cardStyle, marginBottom: 16 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 600 }}>Start from a template</h3>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            {mapTemplates.length > 0 && (
              <>
                <div>
                  <label style={labelStyle}>Template</label>
                  <select
                    value={pickedTemplate}
                    onChange={(e) => setPickedTemplate(e.target.value)}
                    style={{ ...inputStyle, width: 260 }}
                  >
                    <option value="">Choose a template…</option>
                    {mapTemplates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}{t.itemCount ? ` (${t.itemCount} item${t.itemCount === 1 ? '' : 's'})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  style={primaryBtnStyle}
                  onClick={applyTemplate}
                  disabled={!pickedTemplate || applying}
                >
                  {applying ? 'Adding…' : 'Add to plan'}
                </button>
              </>
            )}
            {data.actions && data.actions.length > 0 && (
              <button
                type="button"
                style={secondaryBtnStyle}
                onClick={saveAsTemplate}
                disabled={savingTemplate}
              >
                {savingTemplate ? 'Saving…' : 'Save this plan as a template'}
              </button>
            )}
          </div>
          {/* Says ADD, not replace, and says it before the click rather
              than in a toast afterwards. Applying to a room that already
              has a plan is the case where "apply" could reasonably be
              read as "overwrite". */}
          <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--room-text-muted, #64748b)' }}>
            Template items are added to this plan, not swapped in — nothing already here is removed.
            Due dates are set relative to today.
          </p>
        </div>
      )}

      {/* Add-new card */}
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 600 }}>Add an action item</h3>
        <input
          type="text"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder="e.g. Send pricing PDF to Sarah, Schedule security review with IT…"
          style={{ ...inputStyle, marginBottom: 12 }}
          onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
        />
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <label style={labelStyle}>Due date</label>
            <input
              type="date"
              value={newDueDate}
              onChange={(e) => setNewDueDate(e.target.value)}
              style={{ ...inputStyle, width: 160 }}
            />
          </div>
          <div>
            <label style={labelStyle}>Owner</label>
            <select value={newOwnerSide} onChange={(e) => setNewOwnerSide(e.target.value)} style={{ ...inputStyle, width: 160 }}>
              <option value="seller">Seller</option>
              <option value="prospect">Prospect</option>
              <option value="shared">Shared</option>
            </select>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--room-text-body)', marginTop: 18 }}>
            <input type="checkbox" checked={newIsInternal} onChange={(e) => setNewIsInternal(e.target.checked)} />
            Internal only (hidden from prospect)
          </label>
          <button
            type="button"
            style={{ ...primaryBtnStyle, marginTop: 18 }}
            onClick={handleCreate}
            disabled={!newTitle.trim() || creating}
          >
            {creating ? 'Adding…' : 'Add'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: '12px 16px', borderRadius: 8, background: 'rgba(239, 68, 68, 0.08)', color: '#dc2626', marginBottom: 16, fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Existing items */}
      {data.actions.length === 0 ? (
        <div style={{ ...cardStyle, color: 'var(--room-text-subtle)', textAlign: 'center', padding: 32 }}>
          No action items yet. Add one above to get started.
        </div>
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {data.actions.map((a) => (
            <ActionRow
              key={a.id}
              action={a}
              expanded={!!openComments[a.id]}
              onToggleComments={() => setOpenComments((prev) => ({ ...prev, [a.id]: !prev[a.id] }))}
              onUpdate={(patch) => updateAction(a.id, patch)}
              onDelete={() => deleteAction(a.id)}
              authToken={authToken}
              spaceId={spaceId}
              onCommentPosted={onReload}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function ActionRow({ action, expanded, onToggleComments, onUpdate, onDelete, authToken, spaceId, onCommentPosted }) {
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(action.title);
  const [editDue, setEditDue] = useState(action.dueDate || '');
  const [editOwner, setEditOwner] = useState(action.ownerSide || 'seller');
  const [editInternal, setEditInternal] = useState(!!action.isInternal);
  const [posting, setPosting] = useState(false);
  const [comment, setComment] = useState('');

  const saveEdit = async () => {
    await onUpdate({
      title: editTitle.trim(),
      dueDate: editDue || null,
      ownerSide: editOwner,
      isInternal: editInternal,
    });
    setEditing(false);
  };

  const postComment = async () => {
    if (!comment.trim()) return;
    setPosting(true);
    try {
      const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/actions/${action.id}/comments`, {
        method: 'POST',
        headers: authHeaders(authToken),
        body: JSON.stringify({ body: comment.trim() }),
      });
      if (!r.ok) throw new Error(`Comment failed (status ${r.status})`);
      setComment('');
      onCommentPosted();
    } catch (err) {
      console.error('[map] comment post failed:', err.message);
    } finally {
      setPosting(false);
    }
  };

  const isDone = action.status === 'done';

  return (
    <li id={`map-action-${action.id}`} style={{ ...cardStyle, padding: '14px 16px' }}>
      {editing ? (
        <div>
          <input
            type="text"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            style={{ ...inputStyle, marginBottom: 10 }}
          />
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <input type="date" value={editDue} onChange={(e) => setEditDue(e.target.value)} style={{ ...inputStyle, width: 160 }} />
            <select value={editOwner} onChange={(e) => setEditOwner(e.target.value)} style={{ ...inputStyle, width: 160 }}>
              <option value="seller">Seller</option>
              <option value="prospect">Prospect</option>
              <option value="shared">Shared</option>
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--room-text-body)' }}>
              <input type="checkbox" checked={editInternal} onChange={(e) => setEditInternal(e.target.checked)} />
              Internal only
            </label>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              <button type="button" style={primaryBtnStyle} onClick={saveEdit}>Save</button>
              <button type="button" style={secondaryBtnStyle} onClick={() => setEditing(false)}>Cancel</button>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <input
            type="checkbox"
            checked={isDone}
            onChange={(e) => onUpdate({ status: e.target.checked ? 'done' : 'open' })}
            style={{ width: 18, height: 18, cursor: 'pointer' }}
            title={isDone ? 'Mark as open' : 'Mark as done'}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 500, color: isDone ? 'var(--room-text-subtle, #94a3b8)' : 'var(--room-text-strong, #0f172a)', textDecoration: isDone ? 'line-through' : 'none' }}>
              {action.title}
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 4, fontSize: 12, color: 'var(--room-text-muted)' }}>
              <span>Owner: {action.ownerSide}</span>
              {action.dueDate && <span>Due {new Date(action.dueDate).toLocaleDateString()}</span>}
              {action.isInternal && (
                <span style={{ background: 'var(--room-card-hover)', color: 'var(--room-text-body)', padding: '1px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                  Internal
                </span>
              )}
            </div>
          </div>
          <button type="button" style={secondaryBtnStyle} onClick={() => setEditing(true)}>Edit</button>
          <button
            type="button"
            style={{ ...secondaryBtnStyle, display: 'inline-flex', alignItems: 'center', gap: 6 }}
            onClick={onToggleComments}
            title="Show/hide comments"
            aria-label={`Show or hide comments (${action.comments?.length || 0})`}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <span>{action.comments?.length || 0}</span>
          </button>
          <button
            type="button"
            style={dangerLinkStyle}
            onClick={onDelete}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.08)';
              e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.55)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--room-card, #fff)';
              e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.3)';
            }}
          >
            Delete
          </button>
        </div>
      )}

      {expanded && !editing && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--room-border-soft, #f1f5f9)' }}>
          {(action.comments || []).length === 0 ? (
            <p style={{ color: 'var(--room-text-subtle)', fontSize: 13, margin: '4px 0 12px' }}>No comments yet.</p>
          ) : (
            <ul style={{ margin: '4px 0 12px', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(action.comments || []).map((c) => (
                <li key={c.id} style={{ background: 'var(--room-surface)', padding: '8px 12px', borderRadius: 6 }}>
                  <div style={{ fontSize: 12, color: 'var(--room-text-muted)', marginBottom: 4 }}>
                    <strong style={{ color: 'var(--room-text-strong)' }}>{c.authorDisplayName || c.authorSide}</strong>
                    {' · '}
                    {new Date(c.createdAt).toLocaleString()}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--room-text-body)', whiteSpace: 'pre-wrap' }}>{c.body}</div>
                </li>
              ))}
            </ul>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Add a comment…"
              style={{ ...inputStyle, flex: 1, minWidth: 200 }}
              onKeyDown={(e) => { if (e.key === 'Enter') postComment(); }}
            />
            <ReviseWithAI
              value={comment}
              setValue={setComment}
              authToken={authToken}
              fieldType="map_comment"
              label="AI"
            />
            <button type="button" style={primaryBtnStyle} onClick={postComment} disabled={!comment.trim() || posting}>
              {posting ? 'Posting…' : 'Post'}
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

// ─── People editor (two-sided ledger) ───────────────────────────

// The server resolves every linked asset against the room and drops
// anything the prospect couldn't open (hidden document, unfinished
// recording). Tell the sender when that happened rather than letting a
// "#Pricing deck" go out as plain text without a word.
function droppedAssetRefsNote(sent, kept) {
  if (!Array.isArray(sent) || sent.length === 0 || !Array.isArray(kept)) return '';
  const keptKeys = new Set(kept.map((r) => `${r.kind}:${r.id}`));
  const dropped = sent.filter((r) => !keptKeys.has(`${r.kind}:${r.id}`));
  if (dropped.length === 0) return '';
  return ` ${dropped.map((r) => r.title).join(', ')} ${dropped.length === 1 ? 'was' : 'were'} not linked — hidden from the prospect or no longer in this room.`;
}

export function PeopleEditor({ data, authToken, spaceId, onReload, uiFlags }) {
  const canMessage = uiFlags?.rooms_seller_messages === true;
  // rooms_message_asset_links: documents / trailers the composer can
  // point at. Empty off-flag, which also hides the picker.
  const roomAssets = uiFlags?.rooms_message_asset_links === true ? roomAssetsFromData(data) : [];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      <PeopleSide
        title="Your team"
        side="seller"
        people={data.people.filter((p) => p.side === 'seller')}
        spaceId={spaceId}
        authToken={authToken}
        onReload={onReload}
      />
      <PeopleSide
        title="Buying committee"
        side="buyer"
        people={data.people.filter((p) => p.side === 'buyer')}
        spaceId={spaceId}
        authToken={authToken}
        onReload={onReload}
        canMessage={canMessage}
        allPeople={data.people || []}
        assets={roomAssets}
      />
    </div>
  );
}

function PeopleSide({ title, side, people, spaceId, authToken, onReload, canMessage = false, allPeople = [], assets = [] }) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [title2, setTitle2] = useState('');
  const [error, setError] = useState(null);

  const reset = () => {
    setName(''); setEmail(''); setRole(''); setTitle2('');
    setAdding(false); setError(null);
  };

  const save = async () => {
    if (!name.trim()) return;
    try {
      const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/people`, {
        method: 'POST',
        headers: authHeaders(authToken),
        body: JSON.stringify({
          side, fullName: name.trim(),
          email: email.trim() || null,
          role: role.trim() || null,
          title: title2.trim() || null,
          ordinal: people.length,
        }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.error || `Add failed (status ${r.status})`);
      }
      reset();
      onReload();
    } catch (err) {
      setError(err.message);
    }
  };

  const removePerson = async (personId) => {
    if (!window.confirm('Remove this person from the Decision Room?')) return;
    try {
      await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/people/${personId}`, {
        method: 'DELETE',
        headers: authHeaders(authToken),
      });
      onReload();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--room-text-muted)' }}>
          {title}
        </h3>
        {!adding && (
          <button type="button" style={{ ...secondaryBtnStyle, padding: '4px 10px', fontSize: 12 }} onClick={() => setAdding(true)}>
            + Add
          </button>
        )}
      </div>

      {adding && (
        <div style={{ background: 'var(--room-surface)', padding: 12, borderRadius: 8, marginBottom: 12 }}>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" style={{ ...inputStyle, marginBottom: 8 }} />
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (optional)" style={{ ...inputStyle, marginBottom: 8 }} />
          <input type="text" value={title2} onChange={(e) => setTitle2(e.target.value)} placeholder="Title (e.g. VP Finance)" style={{ ...inputStyle, marginBottom: 8 }} />
          <input
            type="text"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            placeholder={side === 'seller' ? 'Role (e.g. AE, Solution Engineer)' : 'Role (e.g. Champion, Economic Buyer)'}
            style={{ ...inputStyle, marginBottom: 8 }}
          />
          {error && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 8 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" style={primaryBtnStyle} onClick={save} disabled={!name.trim()}>Add</button>
            <button type="button" style={secondaryBtnStyle} onClick={reset}>Cancel</button>
          </div>
        </div>
      )}

      {people.length === 0 ? (
        <p style={{ color: 'var(--room-text-subtle)', fontSize: 13, margin: 0 }}>No one added yet.</p>
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {people.map((p) => (
            <PersonRow
              key={p.id}
              person={p}
              side={side}
              spaceId={spaceId}
              authToken={authToken}
              onReload={onReload}
              onRemove={() => removePerson(p.id)}
              canMessage={canMessage}
              allPeople={allPeople}
              assets={assets}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

// Per-person row with inline access-level toggle (buyer side only) and
// an expandable section-visibility editor. The toggle gates whether
// this person can forward the Decision Room to others; the visibility
// matrix gates which sections they can see when they open it. Both
// are seller-controlled — never exposed to the prospect side.
function PersonRow({ person, side, spaceId, authToken, onReload, onRemove, canMessage = false, allPeople = [], assets = [] }) {
  const [busy, setBusy] = useState(false);
  const [visExpanded, setVisExpanded] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgDraft, setMsgDraft] = useState('');
  const [msgMentions, setMsgMentions] = useState([]);
  const [msgAssetRefs, setMsgAssetRefs] = useState([]);
  const [msgSending, setMsgSending] = useState(false);
  const [msgStatus, setMsgStatus] = useState(null);

  const sendMessage = async () => {
    const body = msgDraft.trim();
    if (!body) return;
    setMsgSending(true);
    setMsgStatus(null);
    try {
      const assetRefs = filterAssetRefsAgainstBody(body, msgAssetRefs);
      const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/people/${person.id}/message`, {
        method: 'POST',
        headers: authHeaders(authToken),
        body: JSON.stringify({ body, mentions: filterMentionsAgainstBody(body, msgMentions), assetRefs }),
      });
      const payload = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(payload.error || `Send failed (${r.status})`);
      setMsgDraft('');
      setMsgMentions([]);
      setMsgAssetRefs([]);
      const droppedNote = droppedAssetRefsNote(assetRefs, payload.assetRefs);
      setMsgStatus(payload.delivered
        ? { tone: droppedNote ? 'warn' : 'ok', text: `Sent — ${person.fullName} has been emailed a link to the conversation.${droppedNote}` }
        : { tone: 'warn', text: `Saved to the room, but the email wasn't sent (${payload.deliveryReason || 'delivery blocked'}). They'll see it when they next open their link.${droppedNote}` });
    } catch (err) {
      setMsgStatus({ tone: 'error', text: `Couldn't send: ${err.message}` });
    } finally {
      setMsgSending(false);
    }
  };

  const isBuyer = side === 'buyer';
  const accessLevel = person.accessLevel || 'member';
  const overrides = person.visibilityOverrides || {};

  const patch = async (body) => {
    setBusy(true);
    try {
      await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/people/${person.id}`, {
        method: 'PATCH',
        headers: authHeaders(authToken),
        body: JSON.stringify(body),
      });
      onReload();
    } finally {
      setBusy(false);
    }
  };

  const setAccessLevel = (level) => patch({ accessLevel: level });
  const setOverride = (sectionId, value) => {
    const next = { ...overrides };
    if (value === null) delete next[sectionId];
    else next[sectionId] = value;
    patch({ visibilityOverrides: next });
  };

  // Sections that exist in a Decision Room — mirrored from the
  // backend's SECTION_DEFAULTS in routes/buyerSpacesPublic.js.
  const SECTIONS = [
    { id: 'welcome', label: 'Welcome', defaultOn: true },
    { id: 'discovery', label: 'Why We\'re Here', defaultOn: true },
    { id: 'demos', label: 'Demos', defaultOn: true },
    { id: 'documents', label: 'Documents', defaultOn: true },
    { id: 'map', label: 'Mutual Action Plan', defaultOn: true },
    { id: 'people', label: 'People', defaultOn: true },
    { id: 'conversations', label: 'Conversations', defaultOn: true },
    { id: 'roi', label: 'ROI', defaultOn: false },
    { id: 'customer_stories', label: 'Customer Stories', defaultOn: false },
  ];

  return (
    <li style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--room-text-strong)' }}>{person.fullName}</div>
          <div style={{ fontSize: 12, color: 'var(--room-text-muted)' }}>
            {person.title || ''}{person.title && person.role ? ' · ' : ''}{person.role || ''}
          </div>
          {person.email && <div style={{ fontSize: 12, color: 'var(--room-text-subtle)' }}>{person.email}</div>}
          {isBuyer && (
            <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--room-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Access:
              </label>
              <select
                value={accessLevel}
                disabled={busy}
                onChange={(e) => setAccessLevel(e.target.value)}
                style={{
                  padding: '4px 8px', borderRadius: 6,
                  border: '1px solid var(--room-border)',
                  background: 'var(--room-card)', color: 'var(--room-text-strong)',
                  fontSize: 12, fontFamily: 'inherit',
                }}
              >
                <option value="member">Member (view + reply only)</option>
                <option value="admin">Admin (can forward)</option>
              </select>
              <button
                type="button"
                onClick={() => setVisExpanded((v) => !v)}
                style={{
                  background: 'none', border: 'none', padding: '4px 0',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  color: 'var(--room-accent)', fontFamily: 'inherit',
                }}
              >
                {visExpanded ? 'Hide visibility' : 'Visibility'}
              </button>
              {canMessage && person.email && (
                <button
                  type="button"
                  onClick={() => { setMsgOpen((v) => !v); setMsgStatus(null); }}
                  style={{
                    background: 'none', border: 'none', padding: '4px 0',
                    fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    color: 'var(--room-accent)', fontFamily: 'inherit',
                  }}
                >
                  {msgOpen ? 'Close message' : 'Message'}
                </button>
              )}
            </div>
          )}
        </div>
        <button
          type="button"
          style={{ ...dangerLinkStyle, fontSize: 12, padding: '6px 12px' }}
          onClick={onRemove}
        >
          Remove
        </button>
      </div>

      {isBuyer && canMessage && msgOpen && (
        <div style={{
          padding: 12, borderRadius: 8,
          background: 'var(--room-surface)',
          border: '1px solid var(--room-border)',
          display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--room-text-muted)' }}>
            Message {person.fullName}
          </div>
          <MentionInput
            value={msgDraft}
            onChange={setMsgDraft}
            mentions={msgMentions}
            onMentionsChange={setMsgMentions}
            people={allPeople}
            assets={assets}
            assetRefs={msgAssetRefs}
            onAssetRefsChange={setMsgAssetRefs}
            placeholder={`Write to ${person.fullName.split(' ')[0]}… they'll get an email with a link to reply in the room`}
            rows={3}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); sendMessage(); }
            }}
          />
          {msgStatus && (
            <div style={{
              fontSize: 12,
              color: msgStatus.tone === 'error' ? '#dc2626' : (msgStatus.tone === 'warn' ? '#a16207' : '#16a34a'),
            }}>
              {msgStatus.text}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: 'var(--room-text-subtle)', marginRight: 'auto' }}>⌘+Enter to send</span>
            <button type="button" style={primaryBtnStyle} onClick={sendMessage} disabled={!msgDraft.trim() || msgSending}>
              {msgSending ? 'Sending…' : 'Send'}
            </button>
          </div>
        </div>
      )}

      {/* Per-person section-visibility matrix. Tri-state per section:
          inherit (uses room default), show, hide. Stored as a JSONB
          override on buyer_space_people; missing keys mean "use room
          default". The backend merges room defaults with this map
          when computing what the prospect sees. */}
      {isBuyer && visExpanded && (
        <div style={{
          padding: 12, borderRadius: 8,
          background: 'var(--room-surface)',
          border: '1px solid var(--room-border)',
          fontSize: 13,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--room-text-muted)', marginBottom: 8 }}>
            Section visibility for {person.fullName}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {SECTIONS.map((s) => {
              const ov = overrides[s.id];
              const currentValue = ov === undefined ? 'inherit' : (ov ? 'show' : 'hide');
              return (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ color: 'var(--room-text-body)' }}>{s.label}</span>
                  <select
                    value={currentValue}
                    disabled={busy}
                    onChange={(e) => {
                      const v = e.target.value;
                      setOverride(s.id, v === 'inherit' ? null : v === 'show');
                    }}
                    style={{
                      padding: '3px 6px', borderRadius: 6,
                      border: '1px solid var(--room-border)',
                      background: 'var(--room-card)', color: 'var(--room-text-strong)',
                      fontSize: 12, fontFamily: 'inherit',
                    }}
                  >
                    <option value="inherit">Default ({s.defaultOn ? 'on' : 'off'})</option>
                    <option value="show">Show</option>
                    <option value="hide">Hide</option>
                  </select>
                </div>
              );
            })}
          </div>
          <p style={{ margin: '10px 0 0', fontSize: 11, color: 'var(--room-text-subtle)' }}>
            Per-person overrides override the room default. Use this to hide pricing from procurement, hide ROI from the champion, etc.
          </p>
        </div>
      )}
    </li>
  );
}

// ─── Documents editor ──────────────────────────────────────────
// Used for both the Documents section (category: general / pricing /
// security / contract) and the Customer Stories section (category:
// customer_story). Caller passes `categoryFilter` so each section
// scopes to the right subset.

const DOC_CATEGORIES = [
  { value: 'general',         label: 'General' },
  { value: 'pricing',         label: 'Pricing' },
  { value: 'security',        label: 'Security' },
  { value: 'contract',        label: 'Contract' },
  { value: 'customer_story',  label: 'Customer story' },
];

const ACCEPT_MIMES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'image/png', 'image/jpeg', 'image/webp', 'image/gif',
  'text/plain', 'text/csv',
  'application/zip',
].join(',');

function fmtDocDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// How many internal links we resolve at once when the section mounts.
// Each doc needs its own (artifact_id is one document), so a room with
// a dozen files would otherwise open a dozen simultaneous requests —
// enough to starve the payload refetch behind them on HTTP/1.1 and to
// hit the API's per-IP ceiling for no gain, since only the first
// screenful of thumbnails is visible anyway.
const SHARE_RESOLVE_CONCURRENCY = 4;

export function DocumentsEditor({
  data, authToken, spaceId, currentUserName, onReload, categoryFilter = null,
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState(categoryFilter || 'general');
  const [error, setError] = useState(null);
  const [showBrowseLibrary, setShowBrowseLibrary] = useState(false);

  // Filter docs to whatever section we're rendering. categoryFilter
  // null = "Documents" section (everything except customer_story).
  // categoryFilter='customer_story' = "Customer Stories" section.
  const filteredDocs = (data.docs || []).filter((d) =>
    categoryFilter ? d.category === categoryFilter : d.category !== 'customer_story'
  );

  // ── The seller's own credential for their own documents ─────────
  //
  // The cards below need two things the seller's Bearer token cannot
  // buy: a page-1 thumbnail and a standalone reading page. Neither has
  // an auth-gated route — `/docs/:docId/download` is the ONLY per-doc
  // endpoint behind requireAuth.
  //
  // This USED to reach for the room's `/preview-token`, which returns
  // the PROSPECT's own distribution token whenever the room has a live
  // one. A rep skimming their own pricing deck therefore posted
  // reading time, per-page dwell and a completion onto that buyer's
  // engagement record, and the rep later read "Acme spent 4 minutes on
  // page 3" about themselves. It was papered over with a `#preview`
  // hash the reading page checked before beaconing — which any copied,
  // shared or hash-stripped URL defeated.
  //
  // Now each card gets its own INTERNAL share link
  // (artifactType 'buyer_space_doc'). Views through `/api/internal/*`
  // land in internal_share_views and that router has no code path to
  // the buyer engagement tables at all, so the isolation survives the
  // URL being copied, forwarded or edited.
  //
  // Two consequences worth knowing, both improvements:
  //   · No section-visibility or prospect_hidden gate. Those are ROOM
  //     rules and the internal router deliberately does not apply them
  //     (see internalRoomDocRow) — so a doc staged hidden, or one in a
  //     section switched off for the buyer, is now previewable by the
  //     seller instead of showing a disabled View.
  //   · Nothing here needs the room slug any more.
  const previewLabel = selfPreviewLabel(currentUserName);

  // docId → { state: 'loading' | 'ready' | 'error', token }
  const [shares, setShares] = useState({});

  // Which docs could actually spend a link: one that shows a thumbnail
  // or opens in the reader. A room of Office attachments mints nothing.
  // Left in card order, so the worker pool below fills the first
  // screenful of thumbnails before the ones nobody has scrolled to.
  const shareTargets = filteredDocs
    .filter((d) => d.hasThumbnail || d.canView)
    .map((d) => d.id);
  // Effects compare by identity, so the LIST cannot be the dependency —
  // a new array every render would re-run this forever. The joined key
  // changes only when the set of docs needing a link changes.
  const shareTargetKey = shareTargets.join(',');

  // A REF, not the state map, is the "already asked" guard. Reading
  // `shares` here instead would put it in the dependency array and the
  // effect's own setShares would re-run it, letting the cleanup cancel
  // the request it just started. Keyed by spaceId so navigating to
  // another room asks again. React 18 makes a setState on an unmounted
  // component a silent no-op, so no cancellation flag is needed.
  const askedRef = useRef({ spaceId: null, ids: new Set() });

  useEffect(() => {
    if (askedRef.current.spaceId !== spaceId) {
      askedRef.current = { spaceId, ids: new Set() };
      setShares({});
    }
    const pending = shareTargets.filter((id) => !askedRef.current.ids.has(id));
    if (pending.length === 0) return undefined;
    pending.forEach((id) => askedRef.current.ids.add(id));

    const controller = new AbortController();
    // Everything this run is responsible for. Anything still here when
    // the effect is torn down (a doc uploaded mid-resolve changes the
    // key and re-runs us) never got an answer, so the guard has to be
    // released for it or its card sits on "Preparing preview…" until
    // the page is reloaded.
    const inFlight = new Set(pending);
    setShares((prev) => {
      const next = { ...prev };
      pending.forEach((id) => { next[id] = { state: 'loading', token: null }; });
      return next;
    });

    // A fixed pool of workers pulling off one shared queue, rather than
    // fixed-size batches: a single slow document can't hold the next
    // batch hostage, and the pool drains in card order.
    const queue = [...pending];
    const worker = async () => {
      for (let id = queue.shift(); id !== undefined; id = queue.shift()) {
        try {
          const token = await resolveInternalShareToken({
            apiBase: API_BASE,
            authToken,
            artifactType: 'buyer_space_doc',
            artifactId: id,
            recipientLabel: previewLabel,
            signal: controller.signal,
          });
          inFlight.delete(id);
          setShares((prev) => ({ ...prev, [id]: { state: 'ready', token } }));
        } catch (err) {
          if (controller.signal.aborted) return;
          inFlight.delete(id);
          console.warn('[docs] internal link failed:', err.message);
          // Drop the guard so a remount (or a section switch back)
          // retries rather than leaving View dead until a reload.
          askedRef.current.ids.delete(id);
          setShares((prev) => ({ ...prev, [id]: { state: 'error', token: null } }));
        }
      }
    };
    Array.from(
      { length: Math.min(SHARE_RESOLVE_CONCURRENCY, queue.length) },
      () => worker(),
    );

    const asked = askedRef.current;
    return () => {
      controller.abort();
      inFlight.forEach((id) => asked.ids.delete(id));
    };
    // shareTargets is derived from shareTargetKey; see the note above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareTargetKey, spaceId, authToken, previewLabel]);

  const handleFile = async (file) => {
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', uploadCategory);
      const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/docs`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
        body: fd,
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.error || `Upload failed (${r.status})`);
      }
      onReload();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document? This cannot be undone.')) return;
    try {
      const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/docs/${docId}`, {
        method: 'DELETE',
        headers: authHeaders(authToken, false),
      });
      if (!r.ok) throw new Error(`Delete failed (${r.status})`);
      onReload();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDownload = (doc) => {
    // Spec: tab open + auth header → forced via fetch + Blob URL so
    // the browser saves with the original filename. Could also use a
    // signed-URL pattern later if R2 issues become a bottleneck.
    fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/docs/${doc.id}/download`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error(`Download failed (${r.status})`);
        return r.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.filename || 'download';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch((err) => setError(err.message));
  };

  return (
    <div>
      {/* Upload card */}
      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <label
            htmlFor={`doc-upload-${categoryFilter || 'general'}`}
            style={{
              ...primaryBtnStyle,
              display: 'inline-flex', alignItems: 'center', gap: 8,
              opacity: uploading ? 0.6 : 1, cursor: uploading ? 'wait' : 'pointer',
            }}
          >
            {uploading ? 'Uploading…' : '+ Upload document'}
          </label>
          <input
            id={`doc-upload-${categoryFilter || 'general'}`}
            type="file"
            accept={ACCEPT_MIMES}
            disabled={uploading}
            onChange={(e) => handleFile(e.target.files?.[0])}
            style={{ display: 'none' }}
          />
          {/* Customer Stories section locks the category to customer_story
              so re-classification has to happen via the metadata edit. */}
          {!categoryFilter && (
            <>
              <span style={{ fontSize: 13, color: 'var(--room-text-muted)' }}>Category:</span>
              <select
                value={uploadCategory}
                onChange={(e) => setUploadCategory(e.target.value)}
                style={{ ...inputStyle, width: 180 }}
              >
                {DOC_CATEGORIES.filter((c) => c.value !== 'customer_story').map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </>
          )}
          <button
            type="button"
            onClick={() => setShowBrowseLibrary(true)}
            style={{
              padding: '8px 14px', borderRadius: 8,
              border: '1px solid #c4b5fd', background: 'var(--room-accent-soft, #faf5ff)', color: '#7c3aed',
              fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
            }}
          >
            Browse Library
          </button>
          <span style={{ fontSize: 12, color: 'var(--room-text-subtle)', marginLeft: 'auto' }}>
            PDF · DOCX · XLSX · PPTX · PNG · JPG up to 25 MB
          </span>
        </div>
      </div>

      {showBrowseLibrary && (
        <BrowseLibraryModal
          spaceId={spaceId}
          authToken={authToken}
          onClose={() => setShowBrowseLibrary(false)}
          onAttached={() => { setShowBrowseLibrary(false); onReload(); }}
        />
      )}

      {error && (
        <div style={{ padding: '12px 16px', borderRadius: 8, background: 'rgba(239, 68, 68, 0.08)', color: '#dc2626', marginBottom: 16, fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* Card grid — same visual language the prospect gets, plus the
          seller-only controls (visibility, edit, delete) the buyer
          never sees. */}
      {filteredDocs.length === 0 ? (
        <div style={{ ...cardStyle, color: 'var(--room-text-subtle)', textAlign: 'center', padding: 32 }}>
          {categoryFilter === 'customer_story'
            ? 'No customer stories yet. Upload a case study or a reference one-pager above.'
            : 'No documents yet. Upload pricing PDFs, decks, security questionnaires, or contracts above.'}
        </div>
      ) : (
        <ul
          aria-label={`${categoryFilter === 'customer_story' ? 'Customer stories' : 'Documents'} — ${filteredDocs.length} document${filteredDocs.length === 1 ? '' : 's'}`}
          style={{
            display: 'grid',
            // min() so a container narrower than 240px (the sidebar-ed
            // room editor on a phone) still yields ONE column instead
            // of a 240px track that overflows the page.
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(240px, 100%), 1fr))',
            gap: 16, margin: 0, padding: 0, listStyle: 'none',
          }}
        >
          {filteredDocs.map((d) => (
            <DocCard
              key={d.id}
              doc={d}
              authToken={authToken}
              spaceId={spaceId}
              share={shares[d.id] || null}
              onReload={onReload}
              onDelete={() => handleDelete(d.id)}
              onDownload={() => handleDownload(d)}
              showCategory={!categoryFilter}
            />
          ))}
        </ul>
      )}

      {/* eSignature tracking — only on the main Documents section,
          not on Customer Stories. Lets the AE attach an external
          DocuSign envelope to this Space and watch engagement
          (sent / first viewed / signed) without leaving TrailerCast.
          Auto-fires the Handoff Brief when the envelope completes. */}
      {!categoryFilter && (
        <EsignTrackedEnvelopes
          authToken={authToken}
          spaceId={spaceId}
        />
      )}
    </div>
  );
}

// ─── One document, as a card (2026-08-23) ───────────────────────
//
// Was `DocRow`: a flat list line whose only affordance was Download,
// which is precisely the thing the prospect's grid stopped doing in
// PR #27. The seller now sees what the buyer sees — thumbnail, page
// count, View — with the seller-only controls (visibility, edit,
// delete) stacked underneath instead of replacing them.
//
// Every seller affordance the row had is still here:
//   inline edit (title / description / category), the eye toggle for
//   prospect_hidden with its dimmed-card treatment, the "From
//   Library" / pinned-version badge, the category chip, Download,
//   Delete. New: the thumbnail well, the friendly type + page count
//   meta line, the upload date, a "From prospect" badge for
//   buyer-uploaded files (the payload always carried
//   uploadedByProspect; the row never showed it), and View.
function DocCard({
  doc, authToken, spaceId, share,
  onReload, onDelete, onDownload, showCategory,
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(doc.displayTitle || doc.filename);
  const [description, setDescription] = useState(doc.description || '');
  const [category, setCategory] = useState(doc.category || 'general');
  // Local mirror so the eye-toggle reflects immediately on click
  // instead of waiting for the next onReload roundtrip. The PATCH
  // is fire-and-forget but onReload is still called so other
  // surfaces (engagement panel, public view) see the change.
  const [hidden, setHidden] = useState(!!doc.prospectHidden);
  // A /thumb that 404s at request time (evicted blob, a render that
  // lost its thumbnail) must never leave a broken-image glyph on the
  // card — it falls back to the same typed placeholder the
  // no-thumbnail case uses.
  const [thumbBroken, setThumbBroken] = useState(false);

  const kind = fileKind(doc.mimeType, doc.filename);
  const displayTitle = doc.displayTitle || doc.filename;

  // Both the thumbnail and View ride this card's own INTERNAL share
  // token (see the block in DocumentsEditor). Neither depends on the
  // document being served to the prospect any more: the internal
  // router applies no section-visibility or prospect_hidden gate, so a
  // doc the seller has staged hidden — or one in a section switched
  // off for the buyer — still previews. That is the point of the type:
  // reviewing a draft before it goes live is exactly this case.
  const shareToken = (share && share.token) || null;
  const thumbUrl = shareToken
    ? `${internalDocBase(API_BASE, shareToken)}/thumb`
    : null;
  const showThumb = Boolean(doc.hasThumbnail) && Boolean(thumbUrl) && !thumbBroken;

  // /i/<token> — the internal viewer. No `#preview` marker: the
  // isolation is structural now, not a hash this URL has to keep
  // carrying, so copying or forwarding it cannot turn it back into a
  // buyer-tracked read.
  const viewHref = doc.canView && shareToken ? internalShareUrl(shareToken) : null;

  // Why View isn't offered — shown as a tooltip on the disabled
  // control so "where's my View button" is answerable in one hover.
  let viewBlockedReason = null;
  if (!doc.canView) {
    viewBlockedReason = 'This file type has no in-browser reader — download it to open it.';
  } else if (share && share.state === 'error') {
    viewBlockedReason = 'Could not open a preview for this document. Reload the page and try again.';
  } else if (!shareToken) {
    viewBlockedReason = 'Preparing preview…';
  }

  const metaBits = [
    kind.label,
    formatPageCount(doc.pageCount),
    formatBytes(doc.sizeBytes),
  ].filter(Boolean);

  const save = async () => {
    try {
      const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/docs/${doc.id}`, {
        method: 'PATCH',
        headers: authHeaders(authToken),
        body: JSON.stringify({ displayTitle: title, description, category }),
      });
      if (!r.ok) throw new Error(`Save failed (${r.status})`);
      setEditing(false);
      onReload();
    } catch (err) {
      console.error('[docs] save failed:', err.message);
    }
  };

  // Toggle prospect visibility. Pure optimistic — flip local state
  // right away so the UI reads as instant, then PATCH in the
  // background. Skips onReload deliberately: nothing else on the
  // seller editor depends on the visibility flag, and reloading the
  // whole Space caused a screen flash on every click. Roll back if
  // the PATCH errors so the UI matches what the server thinks.
  const toggleVisibility = async () => {
    const next = !hidden;
    setHidden(next);
    try {
      const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/docs/${doc.id}`, {
        method: 'PATCH',
        headers: authHeaders(authToken),
        body: JSON.stringify({ prospectHidden: next }),
      });
      if (!r.ok) throw new Error(`Save failed (${r.status})`);
    } catch (err) {
      console.error('[docs] visibility toggle failed:', err.message);
      setHidden(!next);
    }
  };

  const shellStyle = {
    display: 'flex', flexDirection: 'column', minWidth: 0,
    background: 'var(--room-card, #ffffff)',
    border: '1px solid var(--room-border, #e2e8f0)',
    borderRadius: 14, overflow: 'hidden',
    color: 'var(--room-text-strong, #0f172a)',
  };

  if (editing) {
    return (
      <li style={{ ...shellStyle, padding: 14, gap: 10 }}>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Display title"
          style={inputStyle}
        />
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Short description (optional)"
          rows={3}
          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
        />
        <select value={category} onChange={(e) => setCategory(e.target.value)} style={inputStyle}>
          {DOC_CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" style={{ ...primaryBtnStyle, padding: '8px 14px', fontSize: 13 }} onClick={save}>Save</button>
          <button type="button" style={{ ...secondaryBtnStyle, padding: '8px 14px', fontSize: 13 }} onClick={() => setEditing(false)}>Cancel</button>
        </div>
      </li>
    );
  }

  return (
    <li style={{
      ...shellStyle,
      // Dim the whole card when hidden from the prospect so the seller
      // sees at a glance which docs the buying committee won't see.
      opacity: hidden ? 0.62 : 1,
      transition: 'opacity 0.15s ease',
    }}>
      {/* Thumbnail well. Decorative by construction: the accessible
          name for this document is the heading immediately below, so a
          screen reader announces the title once instead of
          "image, image, <title>". */}
      <div aria-hidden="true" style={{
        height: 148, flex: 'none', position: 'relative',
        borderBottom: '1px solid var(--room-border-soft, #f1f5f9)',
        background: showThumb
          ? 'var(--room-surface, #f8fafc)'
          // The type accent survives as a TINT in both themes; the
          // glyph and label do not — docDisplay's accents are 600-
          // weight hexes chosen for the prospect's pinned-light
          // surfaces, and slate-600 on a dark card is unreadable. So
          // the ink here is a --room-* token and the color signal
          // rides the wash.
          : `linear-gradient(135deg, ${kind.accent}24 0%, ${kind.accent}45 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {showThumb ? (
          <img
            src={thumbUrl}
            alt=""
            loading="lazy"
            decoding="async"
            onError={() => setThumbBroken(true)}
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--room-text-muted, #64748b)' }}>
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            <span style={{
              fontSize: 11, fontWeight: 700, letterSpacing: '0.09em',
              textTransform: 'uppercase', color: 'var(--room-text-body, #334155)',
            }}>
              {kind.label}
            </span>
          </div>
        )}
      </div>

      <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 0 }}>
        <h3 style={{
          margin: 0, fontSize: 14.5, fontWeight: 600,
          color: 'var(--room-text-strong, #0f172a)',
          lineHeight: 1.35, minWidth: 0, overflowWrap: 'anywhere',
        }}>
          {displayTitle}
        </h3>

        {/* Badge rail — library provenance, prospect visibility,
            buyer-uploaded, and the category chip all share one wrapping
            line so a card with four of them still has one action row. */}
        {(doc.libraryAssetId || hidden || doc.uploadedByProspect
          || (showCategory && doc.category && doc.category !== 'general')) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {/* Library reference indicator. The doc points at a shared
                library asset; future asset updates flow through unless
                libraryAssetVersion is set (which means the rep pinned
                this attachment to that version). */}
            {doc.libraryAssetId && (
              <span
                style={{ ...docBadgeStyle, background: '#ede9fe', color: '#5b21b6' }}
                title={doc.libraryAssetVersion
                  ? `Pinned to v${doc.libraryAssetVersion} from the company library`
                  : 'Always-latest from the company library — updates automatically'}
              >
                {doc.libraryAssetVersion ? `Library · v${doc.libraryAssetVersion}` : 'From Library'}
              </span>
            )}
            {doc.uploadedByProspect && (
              <span style={{ ...docBadgeStyle, background: '#e0f2fe', color: '#075985' }} title="Uploaded by someone on the buying side">
                From prospect
              </span>
            )}
            {hidden && (
              <span style={{
                ...docBadgeStyle,
                background: 'rgba(148, 163, 184, 0.18)',
                color: 'var(--room-text-body, #334155)',
              }}>
                Hidden from prospect
              </span>
            )}
            {showCategory && doc.category && doc.category !== 'general' && (
              <span style={{
                ...docBadgeStyle,
                background: 'var(--room-card-hover, #f8fafc)',
                color: 'var(--room-text-body, #334155)',
                border: '1px solid var(--room-border, #e2e8f0)',
              }}>
                {(DOC_CATEGORIES.find((c) => c.value === doc.category) || {}).label || doc.category}
              </span>
            )}
          </div>
        )}

        {doc.description && (
          <div style={{
            fontSize: 12.5, color: 'var(--room-text-muted, #64748b)', lineHeight: 1.5,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {doc.description}
          </div>
        )}

        <div style={{ fontSize: 11.5, color: 'var(--room-text-subtle, #94a3b8)' }}>
          {metaBits.join(' · ')}
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--room-text-subtle, #94a3b8)' }}>
          {fmtDocDate(doc.uploadedAt)}
          {/* Rendering is async at upload: say so rather than let the
              seller read a missing page count as a broken document. */}
          {doc.renderStatus === 'pending' && ' · preview rendering…'}
        </div>

        {/* Buyer-facing actions, pinned to the bottom so a grid of
            cards with different description lengths still lines up. */}
        <div style={{ marginTop: 'auto', paddingTop: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {viewHref ? (
            // A real anchor, not window.open: the destination is known
            // synchronously at click time, and a genuine target=_blank
            // link is never popup-blocked, is middle-clickable, and is
            // keyboard-operable for free.
            <a
              href={viewHref}
              target="_blank"
              rel="noopener noreferrer"
              title={`Open ${displayTitle} on its own page`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', borderRadius: 8,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
                color: '#fff', textDecoration: 'none',
                fontSize: 13, fontWeight: 600,
              }}
            >
              <EyeIcon size={14} />
              View
            </a>
          ) : (
            <button
              type="button"
              disabled
              title={viewBlockedReason || 'Preview unavailable'}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', borderRadius: 8,
                border: '1px solid var(--room-border, #e2e8f0)',
                background: 'var(--room-surface, #f8fafc)',
                color: 'var(--room-text-subtle, #94a3b8)',
                fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                cursor: 'not-allowed',
              }}
            >
              <EyeIcon size={14} />
              View
            </button>
          )}
          <button
            type="button"
            onClick={onDownload}
            title={`Download ${doc.filename || displayTitle}`}
            style={{
              padding: '8px 12px', borderRadius: 8,
              background: 'transparent', border: '1px solid transparent',
              color: 'var(--room-text-body, #475569)', cursor: 'pointer',
              fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
            }}
          >
            Download
          </button>
        </div>
      </div>

      {/* Seller-only controls, fenced off below a hairline so the
          buyer-facing actions above read as the buyer's experience. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
        padding: '10px 14px',
        borderTop: '1px solid var(--room-border-soft, #f1f5f9)',
        background: 'var(--room-surface, #f8fafc)',
      }}>
        {/* Eye-toggle for per-doc visibility. Filled-eye = visible
            to prospect; slashed-eye = hidden. */}
        <button
          type="button"
          style={{
            ...visibilityToggleStyle,
            width: 32, height: 32,
            color: hidden ? 'var(--room-text-subtle, #94a3b8)' : '#7c3aed',
            background: hidden ? 'var(--room-surface, #f8fafc)' : 'var(--room-card, #fff)',
          }}
          onClick={toggleVisibility}
          title={hidden ? 'Hidden from prospect — click to show' : 'Visible to prospect — click to hide'}
          aria-label={hidden ? 'Show document to prospect' : 'Hide document from prospect'}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            {hidden ? (
              <>
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                <line x1="1" y1="1" x2="23" y2="23" />
              </>
            ) : (
              <>
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </>
            )}
          </svg>
        </button>
        <button
          type="button"
          style={{ ...secondaryBtnStyle, padding: '7px 12px', fontSize: 13 }}
          onClick={() => setEditing(true)}
        >
          Edit
        </button>
        <button
          type="button"
          style={{ ...dangerLinkStyle, marginLeft: 'auto', padding: '7px 12px' }}
          onClick={onDelete}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(239, 68, 68, 0.08)';
            e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.55)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'var(--room-card, #fff)';
            e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.3)';
          }}
        >
          Delete
        </button>
      </div>
    </li>
  );
}

// Shared chip geometry for the card's badge rail. Colors are supplied
// per-badge: the two semantic ones (library purple, prospect blue) stay
// hardcoded as self-consistent pastel-bg/dark-ink PAIRS per the theming
// rules; the neutral ones use --room-* tokens.
const docBadgeStyle = {
  padding: '2px 8px', borderRadius: 999, fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 10,
  whiteSpace: 'nowrap',
};

function EyeIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

// ─── Engagement panel (seller-only) ─────────────────────────────
// Cross-distribution roll-up: who from the buying committee has
// engaged with the Space, what they did, and when. Backed by
// GET /api/buyer-spaces/:id/engagement.
//
// Layout: 4 stat tiles (aggregate health) → stakeholder grid (one
// row per buyer-side person with their engagement metrics) →
// activity timeline (chronological feed of every meaningful event,
// capped at 50). The trio answers "is this deal alive?" in five
// seconds without forcing the seller to dig through per-distribution
// panels.

export function EngagementPanel({ data, authToken, spaceId }) {
  // Revoke a stakeholder's share-link access. Posts to
  // DELETE /api/trailers/:id for every active distribution they have
  // (a stakeholder can hold multiple — one per demo in the Space —
  // and revoking just one would leave the others as a back door).
  // Wrapped in a confirm because revocation is permanent (the row
  // is preserved for analytics, but the link returns 404 forever).
  const revokeStakeholder = async (stakeholder) => {
    const ids = stakeholder.activeDistributionIds || [];
    if (ids.length === 0) return;
    const label = stakeholder.fullName || stakeholder.email || 'this stakeholder';
    if (!window.confirm(
      `Revoke ${label}'s access?\n\n` +
      `Their share link${ids.length > 1 ? 's' : ''} will return 404 immediately. ` +
      `Engagement history is preserved. This cannot be undone — to restore access, ` +
      `you'll need to send a new share link.`
    )) return;
    try {
      // Sequential rather than parallel — small N, friendlier on the
      // audit log to keep the events ordered.
      for (const id of ids) {
        const r = await fetch(`${API_BASE}/api/trailers/${encodeURIComponent(id)}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!r.ok) {
          const e = await r.json().catch(() => ({}));
          throw new Error(e.error || `Revoke failed (${r.status})`);
        }
      }
      // Refetch the engagement panel so the revoked dists drop out
      // of activeDistributionIds (button hides itself).
      load();
    } catch (err) {
      alert(`Couldn't revoke: ${err.message}`);
    }
  };
  const [engagement, setEngagement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/engagement`, {
        headers: authHeaders(authToken, false),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.error || `Failed to load (${r.status})`);
      }
      const body = await r.json();
      setEngagement(body);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [spaceId]);

  // Only blank on the FIRST load. Subsequent refreshes keep the
  // existing engagement panel mounted so the screen doesn't flash.
  if (loading && !engagement) {
    return <div style={cardStyle}><p style={{ margin: 0, color: 'var(--room-text-subtle)' }}>Loading engagement…</p></div>;
  }
  if (error) {
    return <div style={cardStyle}><p style={{ margin: 0, color: '#dc2626' }}>{error}</p></div>;
  }
  if (!engagement) return null;

  const { aggregate, stakeholders, activity } = engagement;
  const fmtRel = (iso) => {
    if (!iso) return 'never';
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 60_000) return 'just now';
    if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
    if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
    return `${Math.round(ms / 86_400_000)}d ago`;
  };
  const fmtFull = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* ─── Aggregate stat tiles ─────────────────────────────── */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: 12,
      }}>
        <StatTile
          label="Total views"
          value={aggregate.views}
          accent="#7c3aed"
        />
        <StatTile
          label="Stakeholders engaged"
          value={`${aggregate.stakeholdersEngaged}/${aggregate.stakeholdersTotal}`}
          accent="#16a34a"
        />
        <StatTile
          label="Avg completion"
          value={`${aggregate.avgCompletionPct}%`}
          accent="#2563eb"
        />
        <StatTile
          label="Last activity"
          value={fmtRel(aggregate.lastViewAt)}
          accent="#64748b"
        />
      </div>

      {/* ─── Stakeholder grid ─────────────────────────────────── */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 14px', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--room-text-muted)' }}>
          By stakeholder
        </h3>
        {stakeholders.length === 0 ? (
          <p style={{ margin: 0, color: 'var(--room-text-subtle)', fontSize: 13 }}>
            Add buyers on the People tab to track per-stakeholder engagement.
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--room-text-subtle)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <th style={th}>Person</th>
                  <th style={th}>Status</th>
                  <th style={th}>Watch</th>
                  <th style={th}>Last seen</th>
                  <th style={th}>MAP</th>
                  <th style={th}>Messages</th>
                  <th style={th} aria-label="Actions"></th>
                </tr>
              </thead>
              <tbody>
                {stakeholders.map((s) => (
                  <tr key={s.id} style={{ borderTop: '1px solid var(--room-border-soft, #f1f5f9)' }}>
                    <td style={td}>
                      <div style={{ fontWeight: 600, color: 'var(--room-text-strong)' }}>{s.fullName}</div>
                      <div style={{ fontSize: 11, color: 'var(--room-text-subtle)' }}>
                        {s.title || s.role || '—'}
                      </div>
                      {s.forwardedTo && s.forwardedTo.length > 0 && (
                        <div style={{ fontSize: 11, color: '#7c3aed', marginTop: 2 }}>
                          ↳ Forwarded to {s.forwardedTo.length}
                        </div>
                      )}
                    </td>
                    <td style={td}>
                      {s.hasOpened ? (
                        <span style={{ ...statusPill, background: '#ecfdf5', color: '#16a34a' }}>
                          Engaged · {s.viewCount} {s.viewCount === 1 ? 'visit' : 'visits'}
                        </span>
                      ) : (
                        <span style={{ ...statusPill, background: 'var(--room-card-hover)', color: 'var(--room-text-muted)' }}>
                          Not yet opened
                        </span>
                      )}
                    </td>
                    <td style={td}>
                      {s.completionPct > 0 ? `${s.completionPct}%` : '—'}
                    </td>
                    <td style={td} title={fmtFull(s.lastViewedAt)}>
                      {s.lastViewedAt ? fmtRel(s.lastViewedAt) : '—'}
                    </td>
                    <td style={td}>
                      {s.mapCheckoffs > 0 ? (
                        <strong style={{ color: '#16a34a' }}>{s.mapCheckoffs}</strong>
                      ) : <span style={{ color: 'var(--room-border, #cbd5e1)' }}>0</span>}
                    </td>
                    <td style={td}>
                      {s.messagesPosted + s.mapComments > 0 ? (
                        <strong style={{ color: 'var(--room-text-strong)' }}>{s.messagesPosted + s.mapComments}</strong>
                      ) : <span style={{ color: 'var(--room-border, #cbd5e1)' }}>0</span>}
                    </td>
                    <td style={td}>
                      {/* Revoke button — only renders when this
                          stakeholder still has an active share link.
                          Clicking confirms + DELETEs every active
                          distribution so partial revocation can't
                          leave a back door. */}
                      {Array.isArray(s.activeDistributionIds) && s.activeDistributionIds.length > 0 && (
                        <button
                          type="button"
                          onClick={() => revokeStakeholder(s)}
                          title={`Revoke ${s.fullName || s.email || 'this stakeholder'}'s share-link access`}
                          style={{
                            padding: '4px 10px', borderRadius: 6,
                            border: '1px solid #fecaca', background: 'var(--room-card)',
                            color: '#b91c1c', cursor: 'pointer',
                            fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
                            letterSpacing: '0.01em',
                            transition: 'background 0.12s ease',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--room-card, #fff)'; }}
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─── Activity timeline ────────────────────────────────── */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 14px', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--room-text-muted)' }}>
          Recent activity
        </h3>
        {activity.length === 0 ? (
          <p style={{ margin: 0, color: 'var(--room-text-subtle)', fontSize: 13 }}>
            No activity yet. Stakeholder visits, MAP checkoffs, messages, and forwards land here as they happen.
          </p>
        ) : (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {activity.map((ev, i) => (
              <li key={i} style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                paddingBottom: 10,
                borderBottom: i < activity.length - 1 ? '1px solid #f8fafc' : 'none',
              }}>
                <ActivityIcon type={ev.type} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: 'var(--room-text-body)' }}>
                    <strong style={{ color: 'var(--room-text-strong)' }}>{ev.actor}</strong>{' '}
                    <span>{ev.summary}</span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--room-text-subtle)', marginTop: 2 }} title={fmtFull(ev.at)}>
                    {fmtRel(ev.at)}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// Small visual primitives used only by the EngagementPanel — pulled
// out so the main render stays scannable.

function StatTile({ label, value, accent }) {
  return (
    <div style={{
      padding: '14px 18px', borderRadius: 12, background: 'var(--room-card)',
      border: '1px solid var(--room-border)',
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--room-text-muted)' }}>
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color: accent, marginTop: 4 }}>
        {value}
      </div>
    </div>
  );
}

const th = { padding: '8px 10px', fontWeight: 600 };
const td = { padding: '12px 10px', verticalAlign: 'top' };
const statusPill = {
  display: 'inline-block', padding: '2px 10px', borderRadius: 100,
  fontSize: 11, fontWeight: 600, letterSpacing: '0.02em',
};

function ActivityIcon({ type }) {
  // Tiny color-coded square so the seller can scan event types at a
  // glance. Not a true icon set — sized to read inline next to the
  // event text without competing for attention.
  const styleByType = {
    view: { bg: '#ecfdf5', color: '#16a34a', glyph: '👁' },
    complete: { bg: '#eff6ff', color: '#2563eb', glyph: '✓' },
    map_checkoff: { bg: 'var(--room-accent-soft, #faf5ff)', color: '#7c3aed', glyph: '☑' },
    message: { bg: '#fef3c7', color: '#a16207', glyph: '✉' },
    map_comment: { bg: '#fdf2f8', color: '#be185d', glyph: '💬' },
    forward: { bg: 'var(--room-border-soft, #f1f5f9)', color: 'var(--room-text-body)', glyph: '↳' },
  };
  const cfg = styleByType[type] || { bg: 'var(--room-border-soft, #f1f5f9)', color: 'var(--room-text-body)', glyph: '·' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 24, height: 24, borderRadius: 6,
      background: cfg.bg, color: cfg.color,
      fontSize: 12, fontWeight: 700, flex: 'none', marginTop: 1,
    }}>
      {cfg.glyph}
    </span>
  );
}

// ─── Conversations editor (seller side) ─────────────────────────
// Lists every share-message thread on every distribution in this
// Decision Room, lets the seller expand any thread + post a reply.
// Replies POST through the existing /api/trailers/message/:token
// endpoint — the auth header flips it from prospect-post to
// seller-reply automatically. Same wire as the share-page chat,
// just surfaced in a different shell.

export function ConversationsEditor({ data, authToken, spaceId, uiFlags }) {
  // rooms_seller_messages: start a thread from here, delete a message or
  // a whole thread. Off-flag this editor is read-and-reply only.
  const canManage = uiFlags?.rooms_seller_messages === true;
  // rooms_message_asset_links: "#" in any composer here links a room
  // document or trailer; the prospect gets a chip that opens it.
  const roomAssets = uiFlags?.rooms_message_asset_links === true ? roomAssetsFromData(data) : [];
  const buyers = (data.people || []).filter((p) => p.side === 'buyer' && p.email);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composePersonId, setComposePersonId] = useState('');
  const [composeDraft, setComposeDraft] = useState('');
  const [composeMentions, setComposeMentions] = useState([]);
  const [composeAssetRefs, setComposeAssetRefs] = useState([]);
  const [composePosting, setComposePosting] = useState(false);
  const [composeStatus, setComposeStatus] = useState(null);
  const [threads, setThreads] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedKey, setExpandedKey] = useState(null);
  const [replyDraft, setReplyDraft] = useState('');
  const [replyMentions, setReplyMentions] = useState([]);
  const [replyAssetRefs, setReplyAssetRefs] = useState([]);
  const [posting, setPosting] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/conversations`, {
        headers: authHeaders(authToken, false),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.error || `Failed to load (${r.status})`);
      }
      const body = await r.json();
      setThreads(body.threads || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load once on mount; re-fetched after a successful reply.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [spaceId]);

  const sendReply = async (thread) => {
    if (!replyDraft.trim()) return;
    setPosting(true);
    try {
      const finalMentions = filterMentionsAgainstBody(replyDraft, replyMentions);
      const r = await fetch(`${API_BASE}/api/trailers/message/${encodeURIComponent(thread.distributionToken)}`, {
        method: 'POST',
        headers: authHeaders(authToken),
        body: JSON.stringify({
          kind: 'comment',
          body: replyDraft.trim(),
          mentions: finalMentions,
          assetRefs: filterAssetRefsAgainstBody(replyDraft, replyAssetRefs),
          // The seller's auth header is what flips sender_kind to
          // 'seller' on the backend; sender_name pulls from the
          // authenticated user record on that side, so we don't
          // need to send it from here.
        }),
      });
      if (!r.ok) throw new Error(`Reply failed (${r.status})`);
      setReplyDraft('');
      setReplyMentions([]);
      setReplyAssetRefs([]);
      load();
    } catch (err) {
      console.error('[conversations] reply failed:', err.message);
      alert(`Couldn't send: ${err.message}`);
    } finally {
      setPosting(false);
    }
  };

  const sendNew = async () => {
    const body = composeDraft.trim();
    if (!body || !composePersonId) return;
    setComposePosting(true);
    setComposeStatus(null);
    try {
      const assetRefs = filterAssetRefsAgainstBody(body, composeAssetRefs);
      const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/people/${composePersonId}/message`, {
        method: 'POST',
        headers: authHeaders(authToken),
        body: JSON.stringify({ body, mentions: filterMentionsAgainstBody(body, composeMentions), assetRefs }),
      });
      const payload = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(payload.error || `Send failed (${r.status})`);
      setComposeDraft('');
      setComposeMentions([]);
      setComposeAssetRefs([]);
      const droppedNote = droppedAssetRefsNote(assetRefs, payload.assetRefs);
      if (payload.delivered && !droppedNote) {
        setComposeOpen(false);
        setExpandedKey(payload.threadKey || null);
      } else if (payload.delivered) {
        setComposeStatus({ tone: 'warn', text: `Sent.${droppedNote}` });
        setExpandedKey(payload.threadKey || null);
      } else {
        setComposeStatus({ tone: 'warn', text: `Saved to the room, but the email wasn't sent (${payload.deliveryReason || 'delivery blocked'}). They'll see it when they next open their link.${droppedNote}` });
      }
      load();
    } catch (err) {
      setComposeStatus({ tone: 'error', text: `Couldn't send: ${err.message}` });
    } finally {
      setComposePosting(false);
    }
  };

  const deleteMessage = async (m) => {
    if (!window.confirm('Delete this message? It disappears for the prospect too.')) return;
    try {
      const r = await fetch(`${API_BASE}/api/conversations/messages/${encodeURIComponent(m.id)}`, {
        method: 'DELETE',
        headers: authHeaders(authToken, false),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.error || `Delete failed (${r.status})`);
      }
      load();
    } catch (err) {
      alert(`Couldn't delete: ${err.message}`);
    }
  };

  const deleteThread = async (t) => {
    if (!window.confirm(`Delete the whole conversation with ${t.prospectName || t.prospectEmail || 'this prospect'}? Their messages and your replies are removed for both sides.`)) return;
    try {
      const r = await fetch(
        `${API_BASE}/api/conversations/${encodeURIComponent(t.distributionId)}/messages?threadEmail=${encodeURIComponent(t.prospectEmail || '')}`,
        { method: 'DELETE', headers: authHeaders(authToken, false) }
      );
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.error || `Delete failed (${r.status})`);
      }
      setExpandedKey(null);
      load();
    } catch (err) {
      alert(`Couldn't delete: ${err.message}`);
    }
  };

  const header = canManage ? (
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
      {!composeOpen && (
        <button
          type="button"
          style={{ ...secondaryBtnStyle, fontSize: 12, padding: '6px 12px' }}
          onClick={() => { setComposeOpen(true); setComposeStatus(null); if (!composePersonId && buyers[0]) setComposePersonId(buyers[0].id); }}
          disabled={buyers.length === 0}
          title={buyers.length ? '' : 'Add a buyer-side person with an email on the People tab first'}
        >
          + New message
        </button>
      )}
    </div>
  ) : null;

  const composer = canManage && composeOpen ? (
    <div style={{ ...cardStyle, marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--room-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          To:
        </label>
        <select
          value={composePersonId}
          onChange={(e) => setComposePersonId(e.target.value)}
          style={{ ...inputStyle, width: 'auto', minWidth: 220 }}
        >
          {buyers.map((p) => (
            <option key={p.id} value={p.id}>{p.fullName} — {p.email}{p.role ? ` · ${p.role}` : ''}</option>
          ))}
        </select>
      </div>
      <MentionInput
        value={composeDraft}
        onChange={setComposeDraft}
        mentions={composeMentions}
        onMentionsChange={setComposeMentions}
        people={data.people || []}
        assets={roomAssets}
        assetRefs={composeAssetRefs}
        onAssetRefsChange={setComposeAssetRefs}
        placeholder="Write your message… they'll get an email with a link to reply in the room"
        rows={3}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); sendNew(); }
        }}
      />
      {composeStatus && (
        <div style={{ fontSize: 12, color: composeStatus.tone === 'error' ? '#dc2626' : '#a16207' }}>
          {composeStatus.text}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: 'var(--room-text-subtle)', marginRight: 'auto' }}>⌘+Enter to send</span>
        <button type="button" style={secondaryBtnStyle} onClick={() => { setComposeOpen(false); setComposeStatus(null); }}>Cancel</button>
        <button type="button" style={primaryBtnStyle} onClick={sendNew} disabled={!composeDraft.trim() || !composePersonId || composePosting}>
          {composePosting ? 'Sending…' : 'Send'}
        </button>
      </div>
    </div>
  ) : null;

  // Only blank on the FIRST load. After a reply post, the
  // background reload keeps the existing thread UI mounted so the
  // screen doesn't flash.
  if (loading && threads === null) {
    return <div style={cardStyle}><p style={{ margin: 0, color: 'var(--room-text-subtle)' }}>Loading conversations…</p></div>;
  }
  if (error) {
    return <div style={cardStyle}><p style={{ margin: 0, color: '#dc2626' }}>{error}</p></div>;
  }
  if (!threads || threads.length === 0) {
    return (
      <div>
        {header}
        {composer}
        <div style={{ ...cardStyle, color: 'var(--room-text-subtle)', textAlign: 'center', padding: 32 }}>
          {canManage
            ? 'No conversations yet. Start one with + New message, or wait for a stakeholder to write from their Decision Room.'
            : "No prospect messages yet. They'll appear here when a stakeholder posts in their Decision Room."}
        </div>
      </div>
    );
  }

  // The thread's distributionToken comes from listForBuyerSpace
  // (we added the column there). Wire it through so the reply form
  // can target the right /api/trailers/message/<token> endpoint.
  return (
    <div>
    {header}
    {composer}
    <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
      {threads.map((t) => {
        const isOpen = expandedKey === t.threadKey;
        const lastMessage = t.messages?.[0]; // groupIntoThreads sorts newest-first
        return (
          <li key={t.threadKey} style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
            <button
              type="button"
              onClick={() => {
                setExpandedKey(isOpen ? null : t.threadKey);
                setReplyDraft('');
              }}
              style={{
                width: '100%', textAlign: 'left', padding: '14px 18px',
                background: 'transparent', border: 'none', cursor: 'pointer',
                fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 12,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--room-text-strong)' }}>
                  {/* Name AND address, not one or the other. The name is
                      who the seller thinks in; the address is what
                      distinguishes two threads when a person appears
                      twice (a forward, a personal address). The email
                      drops to a muted sibling when both are known, and
                      carries the row on its own when the name isn't. */}
                  {t.prospectName || t.prospectEmail || 'Anonymous prospect'}
                  {t.prospectName && t.prospectEmail && (
                    <span style={{
                      marginLeft: 8, fontSize: 12.5, fontWeight: 400,
                      color: 'var(--room-text-muted)',
                    }}>
                      {t.prospectEmail}
                    </span>
                  )}
                  {t.awaitingSellerReply && (
                    <span style={{
                      marginLeft: 8, padding: '1px 8px', borderRadius: 100,
                      background: '#fef3c7', color: '#a16207',
                      fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
                    }}>
                      AWAITING REPLY
                    </span>
                  )}
                </div>
                <div style={{
                  fontSize: 13, color: 'var(--room-text-muted)', marginTop: 4,
                  overflow: 'hidden', textOverflow: 'ellipsis',
                  display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical',
                }}>
                  {lastMessage?.body || ''}
                </div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--room-text-subtle)', flex: 'none' }}>
                {t.messageCount} message{t.messageCount === 1 ? '' : 's'}
              </div>
              <div style={{ color: 'var(--room-text-subtle)', fontSize: 14 }}>{isOpen ? '▾' : '▸'}</div>
            </button>

            {isOpen && (
              <div style={{ padding: '0 18px 16px', borderTop: '1px solid var(--room-border-soft, #f1f5f9)' }}>
                <ul style={{ margin: '12px 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {/* groupIntoThreads sorts newest-first; reverse for
                      reading order (oldest message at top, newest at
                      the bottom right above the reply form). */}
                  {[...t.messages].reverse().map((m) => {
                    const isFromSeller = m.sender_kind === 'seller';
                    return (
                      <li key={m.id} style={{
                        background: isFromSeller ? 'var(--room-accent-soft, #faf5ff)' : 'var(--room-surface, #f8fafc)',
                        padding: '10px 14px', borderRadius: 8,
                        alignSelf: isFromSeller ? 'flex-end' : 'flex-start',
                        maxWidth: '80%',
                      }}>
                        <div style={{ fontSize: 12, color: 'var(--room-text-muted)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <strong style={{ color: 'var(--room-text-strong)' }}>
                            {isFromSeller ? 'You' : (m.sender_name || t.prospectName || 'Prospect')}
                          </strong>
                          <span>· {new Date(m.created_at).toLocaleString()}</span>
                          {canManage && (
                            <button
                              type="button"
                              onClick={() => deleteMessage(m)}
                              title="Delete this message"
                              aria-label="Delete this message"
                              style={{
                                background: 'none', border: 'none', padding: '0 2px',
                                cursor: 'pointer', color: 'var(--room-text-subtle)',
                                fontSize: 12, fontFamily: 'inherit', lineHeight: 1,
                              }}
                            >
                              🗑
                            </button>
                          )}
                          {typeof m.referencedClipIndex === 'number' && (
                            <span
                              title="This message refers to a specific clip"
                              style={{
                                display: 'inline-flex', alignItems: 'center',
                                padding: '1px 8px', borderRadius: 100,
                                fontSize: 11, fontWeight: 600,
                                background: 'rgba(99, 102, 241, 0.10)',
                                color: '#4f46e5',
                                border: '1px solid rgba(99, 102, 241, 0.22)',
                              }}
                            >
                              Re: {m.referencedClipFeature || `Clip ${m.referencedClipIndex + 1}`}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 14, color: 'var(--room-text-body)', whiteSpace: 'pre-wrap' }}>
                          {renderBodyWithMentions(m.body, m.mentions || [], { assetRefs: m.assetRefs || [] })}
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <MentionInput
                    value={replyDraft}
                    onChange={setReplyDraft}
                    mentions={replyMentions}
                    onMentionsChange={setReplyMentions}
                    people={data.people || []}
                    assets={roomAssets}
                    assetRefs={replyAssetRefs}
                    onAssetRefsChange={setReplyAssetRefs}
                    placeholder="Reply to this prospect… type @ to mention someone"
                    rows={2}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                        e.preventDefault();
                        sendReply(t);
                      }
                    }}
                  />
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: 'var(--room-text-subtle)', marginRight: 'auto' }}>⌘+Enter to send</span>
                    <ReviseWithAI
                      value={replyDraft}
                      setValue={setReplyDraft}
                      authToken={authToken}
                      fieldType="map_comment"
                      label="AI"
                    />
                    <button
                      type="button"
                      style={primaryBtnStyle}
                      onClick={() => sendReply(t)}
                      disabled={!replyDraft.trim() || posting}
                    >
                      {posting ? 'Sending…' : 'Send'}
                    </button>
                  </div>
                </div>
                {canManage && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                    <button
                      type="button"
                      style={{ ...dangerLinkStyle, fontSize: 12, padding: '4px 8px' }}
                      onClick={() => deleteThread(t)}
                    >
                      Delete thread
                    </button>
                  </div>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
    </div>
  );
}

// ─── ROI editor ─────────────────────────────────────────────────

export function RoiEditor({ data, authToken, spaceId, onReload }) {
  const roi = data.roi || {};
  const [currentCost, setCurrentCost] = useState(roi.currentCostCents != null ? (roi.currentCostCents / 100).toString() : '');
  const [savings, setSavings] = useState(roi.projectedSavingsCents != null ? (roi.projectedSavingsCents / 100).toString() : '');
  const [payback, setPayback] = useState(roi.paybackMonths != null ? String(roi.paybackMonths) : '');
  const [justification, setJustification] = useState(roi.justification || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const dollarsToCents = (v) => {
    if (v === '' || v == null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? Math.round(n * 100) : null;
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/roi`, {
        method: 'PUT',
        headers: authHeaders(authToken),
        body: JSON.stringify({
          currentCostCents: dollarsToCents(currentCost),
          projectedSavingsCents: dollarsToCents(savings),
          paybackMonths: payback ? Number(payback) : null,
          justification: justification || null,
        }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.error || `Save failed (status ${r.status})`);
      }
      onReload();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={cardStyle}>
      <p style={{ marginTop: 0, color: 'var(--room-text-muted)', fontSize: 14 }}>
        Quantify the deal. The prospect view turns these into a calculator widget — the seller view stores the raw numbers.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 20 }}>
        <div>
          <label style={labelStyle}>Current annual cost / pain ($)</label>
          <input type="number" value={currentCost} onChange={(e) => setCurrentCost(e.target.value)} placeholder="50000" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Projected annual savings ($)</label>
          <input type="number" value={savings} onChange={(e) => setSavings(e.target.value)} placeholder="35000" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Payback period (months)</label>
          <input type="number" value={payback} onChange={(e) => setPayback(e.target.value)} placeholder="6" style={inputStyle} />
        </div>
      </div>
      <div style={{ marginTop: 20 }}>
        <label style={labelStyle}>Justification / narrative</label>
        <textarea
          value={justification}
          onChange={(e) => setJustification(e.target.value)}
          placeholder="Walk the prospect through how you arrived at these numbers. Reference specific pain points + the demo's automation results."
          rows={6}
          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
        />
      </div>
      {error && <div style={{ color: '#dc2626', marginTop: 12, fontSize: 13 }}>{error}</div>}
      <div style={{ marginTop: 20, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" style={primaryBtnStyle} onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save ROI'}
        </button>
        <ReviseWithAI
          value={justification}
          setValue={setJustification}
          authToken={authToken}
          fieldType="roi_justification"
          label="Revise narrative with AI"
        />
      </div>
    </div>
  );
}

// ─── eSignature tracking ──────────────────────────────────────────
//
// Renders below the document list on the Buyer Space → Documents
// tab. Two states:
//   - No tracked envelopes yet → CTA + form to paste an envelope ID.
//   - Has envelopes → list of cards with engagement metrics.
//
// The "completed" auto-fire (Space → Won + Handoff Brief) happens
// server-side via the eSign webhook receiver; this UI just watches
// the rolled-up status come in.
//
// Spec: backend/docs/specs/ESIGN_TRACKING_SPEC.md
function EsignTrackedEnvelopes({ authToken, spaceId }) {
  const [loading, setLoading] = useState(true);
  const [envelopes, setEnvelopes] = useState([]);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [envelopeId, setEnvelopeId] = useState('');
  const [subject, setSubject] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/esign`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!r.ok) throw new Error(`Load failed (${r.status})`);
      const body = await r.json();
      setEnvelopes(body.envelopes || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [spaceId]);

  const track = async () => {
    if (!envelopeId.trim()) { setError('Envelope ID is required.'); return; }
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/esign/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({
          vendor: 'docusign',
          vendorEnvelopeId: envelopeId.trim(),
          subject: subject.trim() || undefined,
        }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || `Track failed (${r.status})`);
      setEnvelopeId('');
      setSubject('');
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--room-text-strong)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
          External contracts (eSignature)
        </h3>
        {!showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            style={{
              padding: '6px 12px', borderRadius: 8,
              border: '1px solid #c4b5fd', background: 'var(--room-accent-soft, #faf5ff)', color: '#7c3aed',
              fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
            }}
          >
            + Track contract
          </button>
        )}
      </div>

      {showForm && (
        <div style={{ ...cardStyle, padding: 14, marginBottom: 12 }}>
          <p style={{ margin: '0 0 10px', fontSize: 12.5, color: 'var(--room-text-muted)' }}>
            Paste the envelope ID from DocuSign (Manage → Envelope details).
            We'll watch sent / viewed / signed events and auto-fire the
            Handoff Brief when it completes.
          </p>
          <input
            type="text"
            placeholder="Envelope ID (e.g. abc123-...)"
            value={envelopeId}
            onChange={(e) => setEnvelopeId(e.target.value)}
            disabled={busy}
            style={{ ...inputStyle, width: '100%', marginBottom: 8 }}
          />
          <input
            type="text"
            placeholder="Subject (optional, e.g. Acme Corp MSA)"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={busy}
            style={{ ...inputStyle, width: '100%', marginBottom: 8 }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={track}
              disabled={busy || !envelopeId.trim()}
              style={{
                padding: '8px 16px', borderRadius: 8, border: 'none',
                background: '#7c3aed', color: '#fff',
                fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                cursor: busy ? 'wait' : 'pointer',
                opacity: (busy || !envelopeId.trim()) ? 0.55 : 1,
              }}
            >
              {busy ? 'Tracking…' : 'Track this contract'}
            </button>
            <button
              type="button"
              onClick={() => { setShowForm(false); setEnvelopeId(''); setSubject(''); setError(null); }}
              disabled={busy}
              style={{
                padding: '8px 14px', borderRadius: 8,
                border: '1px solid #cbd5e1', background: 'var(--room-card, #fff)', color: 'var(--room-text-strong, #1e293b)',
                fontSize: 13, fontFamily: 'inherit', cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && (
        <div style={{ padding: '8px 12px', borderRadius: 8, background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b', fontSize: 13, marginBottom: 10 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--room-text-muted)', fontSize: 13 }}>Loading envelopes…</div>
      ) : envelopes.length === 0 && !showForm ? (
        <div style={{ ...cardStyle, color: 'var(--room-text-subtle)', textAlign: 'center', padding: 24, fontSize: 13 }}>
          No tracked contracts yet. Click <strong>Track contract</strong> above to attach a DocuSign envelope.
        </div>
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {envelopes.map((e) => (
            <EsignEnvelopeCard
              key={e.id}
              envelope={e}
              authToken={authToken}
              spaceId={spaceId}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function EsignEnvelopeCard({ envelope, authToken, spaceId }) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const tone = STATUS_TONES[envelope.status] || STATUS_TONES.sent;

  const expand = async () => {
    if (expanded) { setExpanded(false); return; }
    setExpanded(true);
    if (detail) return;
    setLoadingDetail(true);
    try {
      const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/esign/${envelope.id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!r.ok) throw new Error(`Load failed (${r.status})`);
      const body = await r.json();
      setDetail(body);
    } catch (err) {
      setDetail({ error: err.message });
    } finally {
      setLoadingDetail(false);
    }
  };

  const fmt = (d) => d ? new Date(d).toLocaleString() : '—';
  const fmtRelative = (d) => {
    if (!d) return null;
    const ms = Date.now() - new Date(d).getTime();
    if (ms < 60_000) return 'just now';
    if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
    if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
    return `${Math.floor(ms / 86_400_000)}d ago`;
  };

  return (
    <li style={{
      ...cardStyle,
      padding: 14,
      borderLeft: `4px solid ${tone.accent}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: tone.bg, color: tone.fg, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              {tone.label}
            </span>
            <span style={{ fontSize: 11, color: 'var(--room-text-subtle)' }}>
              DocuSign · {envelope.vendorEnvelopeId.slice(0, 16)}{envelope.vendorEnvelopeId.length > 16 ? '…' : ''}
            </span>
            {envelope.handoffFiredAt && (
              <span style={{ fontSize: 10, color: '#065f46', background: '#d1fae5', padding: '1px 8px', borderRadius: 999, fontWeight: 600 }}>
                Handoff Brief fired
              </span>
            )}
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--room-text-strong)' }}>
            {envelope.subject || '(No subject yet — webhook will fill this in)'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--room-text-muted)', marginTop: 4 }}>
            {envelope.sentAt && <>Sent {fmt(envelope.sentAt)} · </>}
            {envelope.firstViewedAt && <>First view {fmtRelative(envelope.firstViewedAt)} · </>}
            {envelope.lastEventAt && <>Last event {fmtRelative(envelope.lastEventAt)}</>}
          </div>
          {envelope.completedAt && (
            <div style={{ fontSize: 12, color: '#065f46', marginTop: 2, fontWeight: 600 }}>
              Completed {fmt(envelope.completedAt)}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={expand}
          style={{
            padding: '6px 10px', borderRadius: 6,
            border: '1px solid #cbd5e1', background: 'var(--room-card, #fff)', color: 'var(--room-text-strong, #1e293b)',
            fontSize: 12, fontFamily: 'inherit', cursor: 'pointer',
          }}
        >
          {expanded ? 'Hide' : 'Details'}
        </button>
      </div>

      {expanded && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--room-border-soft)' }}>
          {loadingDetail ? (
            <div style={{ fontSize: 12, color: 'var(--room-text-muted)' }}>Loading event history…</div>
          ) : detail?.error ? (
            <div style={{ fontSize: 12, color: '#991b1b' }}>{detail.error}</div>
          ) : detail ? (
            <>
              {Array.isArray(detail.recipients) && detail.recipients.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--room-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
                    Recipients
                  </div>
                  {detail.recipients.map((r) => (
                    <div key={r.email} style={{ padding: '6px 0', fontSize: 12.5, borderBottom: '1px solid var(--room-border-soft)' }}>
                      <div style={{ fontWeight: 500, color: 'var(--room-text-strong)' }}>
                        {r.name || r.email} <span style={{ color: 'var(--room-text-subtle)', fontWeight: 400 }}>({r.role})</span>
                      </div>
                      <div style={{ color: 'var(--room-text-muted)' }}>
                        {r.signedAt ? <span style={{ color: '#065f46', fontWeight: 600 }}>Signed {fmt(r.signedAt)}</span>
                          : r.declinedAt ? <span style={{ color: '#b91c1c', fontWeight: 600 }}>Declined {fmt(r.declinedAt)}</span>
                          : r.firstViewedAt ? <>Viewed · last seen {fmtRelative(r.lastSeenAt)} · not yet signed</>
                          : 'Not viewed yet'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {Array.isArray(detail.events) && detail.events.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--room-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
                    Recent events
                  </div>
                  {detail.events.slice(0, 8).map((ev) => (
                    <div key={ev.id} style={{ padding: '4px 0', fontSize: 12, color: 'var(--room-text-body)' }}>
                      <span style={{ color: 'var(--room-text-subtle)' }}>{fmt(ev.occurred_at)}</span>
                      {' · '}
                      <strong>{ev.event_type}</strong>
                      {ev.recipient_email ? ` · ${ev.recipient_name || ev.recipient_email}` : ''}
                    </div>
                  ))}
                  {detail.events.length > 8 && (
                    <div style={{ fontSize: 11, color: 'var(--room-text-subtle)', marginTop: 4 }}>
                      + {detail.events.length - 8} earlier events
                    </div>
                  )}
                </div>
              )}
            </>
          ) : null}
        </div>
      )}
    </li>
  );
}

const STATUS_TONES = {
  sent:           { label: 'Sent',           accent: 'var(--room-text-subtle, #94a3b8)', bg: 'var(--room-border-soft, #f1f5f9)', fg: 'var(--room-text-body, #475569)' },
  delivered:      { label: 'Delivered',      accent: '#3b82f6', bg: '#dbeafe', fg: '#1e40af' },
  viewed:         { label: 'Viewed',         accent: '#0ea5e9', bg: '#e0f2fe', fg: '#0c4a6e' },
  signed_partial: { label: 'Partially signed', accent: '#f59e0b', bg: '#fef3c7', fg: '#92400e' },
  completed:      { label: 'Completed',      accent: '#10b981', bg: '#d1fae5', fg: '#065f46' },
  declined:       { label: 'Declined',       accent: '#ef4444', bg: '#fee2e2', fg: '#991b1b' },
  voided:         { label: 'Voided',         accent: 'var(--room-text-muted, #64748b)', bg: 'var(--room-border-soft, #f1f5f9)', fg: 'var(--room-text-body, #334155)' },
};

// ─── Browse Library modal ─────────────────────────────────────────
//
// Opens from the Documents tab → "Browse Library" button. Lets the AE
// pick assets from the company-wide pool and attach them to this Space
// in one click. Loads two streams:
//   - GET /library/assets/suggested?spaceId=X — top 5 ranked for this Space
//   - GET /library/assets — full list with type filter + search
//
// Multi-select; "Add N selected" attaches them all in parallel via
// POST /buyer-spaces/:id/docs/from-library.
function BrowseLibraryModal({ spaceId, authToken, onClose, onAttached }) {
  const [suggested, setSuggested] = useState([]);
  // AI suggestions: opt-in. The modal does NOT auto-trigger an AI run on
  // open — that landed at ~$0.50/rep/day for power users. Instead, we
  // load the cached AI ranking (if any) for free, and give the rep an
  // "Ask AI to rank" button to trigger a fresh run only when they
  // actually want it. Cached results persist across modal opens until
  // the rep explicitly re-runs.
  const [aiSuggested, setAiSuggested] = useState([]);
  const [aiCached, setAiCached] = useState(false);
  const [aiCachedAt, setAiCachedAt] = useState(null);
  const [aiRunning, setAiRunning] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [allAssets, setAllAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState(null);
  const [searchQ, setSearchQ] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [attaching, setAttaching] = useState(false);

  const ASSET_TYPES_LOCAL = [
    ['case_study', 'Case study'],
    ['deck',       'Deck'],
    ['pricing',    'Pricing'],
    ['security',   'Security'],
    ['legal',      'Legal'],
    ['battlecard', 'Battlecard'],
    ['one_pager',  'One-pager'],
    ['other',      'Other'],
  ];

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filterType) params.set('type', filterType);
      if (searchQ.trim()) params.set('q', searchQ.trim());
      // Fire three requests, all FREE:
      //   - filtered library list (re-fetched on filter change)
      //   - cheap FTS suggested list (once per Space)
      //   - cached AI suggestions (once per Space, NO AI call —
      //     server reads from buyer_spaces.ai_library_suggestions)
      // The AI itself only runs when the rep clicks "Ask AI to rank"
      // (POST below), so a casual modal open costs $0.
      const isFirstOpen = suggested.length === 0 && aiSuggested.length === 0;
      const [allR, sugR, aiR] = await Promise.all([
        fetch(`${API_BASE}/api/library/assets?${params.toString()}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        }),
        isFirstOpen
          ? fetch(`${API_BASE}/api/library/assets/suggested?spaceId=${encodeURIComponent(spaceId)}`, {
              headers: { Authorization: `Bearer ${authToken}` },
            })
          : Promise.resolve(null),
        isFirstOpen
          ? fetch(`${API_BASE}/api/library/assets/ai-suggested?spaceId=${encodeURIComponent(spaceId)}`, {
              headers: { Authorization: `Bearer ${authToken}` },
            }).catch(() => null)
          : Promise.resolve(null),
      ]);
      if (!allR.ok) throw new Error(`Library load failed (${allR.status})`);
      const allBody = await allR.json();
      setAllAssets(allBody.assets || []);
      if (sugR && sugR.ok) {
        const sugBody = await sugR.json();
        setSuggested(sugBody.assets || []);
      }
      if (aiR && aiR.ok) {
        const aiBody = await aiR.json();
        setAiSuggested(aiBody.assets || []);
        setAiCached(!!aiBody.cached);
        setAiCachedAt(aiBody.cachedAt || null);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Explicit AI run (paid, ~$0.05). Only fires when the seller clicks
  // "Ask AI to rank" — the cached result feeds the GET above for free.
  const runAi = async () => {
    setAiRunning(true);
    setAiError(null);
    try {
      const r = await fetch(`${API_BASE}/api/library/assets/ai-suggested?spaceId=${encodeURIComponent(spaceId)}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || `AI ranking failed (${r.status})`);
      if (body.error) {
        // 200 with error string = the AI helper threw but the route
        // didn't 500 (so the modal stays usable).
        throw new Error(body.error);
      }
      setAiSuggested(body.assets || []);
      setAiCached(false);
      setAiCachedAt(body.cachedAt || new Date().toISOString());
    } catch (err) {
      setAiError(err.message);
    } finally {
      setAiRunning(false);
    }
  };
  useEffect(() => {
    const t = setTimeout(load, searchQ ? 250 : 0);
    return () => clearTimeout(t);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [filterType, searchQ]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const attachSelected = async () => {
    if (selected.size === 0) return;
    setAttaching(true);
    setError(null);
    try {
      // Sequential to keep error reporting clean — these are quick.
      for (const id of selected) {
        const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/docs/from-library`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({ libraryAssetId: id }),
        });
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.error || `Attach failed for asset ${id}`);
        }
      }
      onAttached();
    } catch (err) {
      setError(err.message);
    } finally {
      setAttaching(false);
    }
  };

  // De-dup the suggested + AI-suggested out of the all-assets list
  // so the same card doesn't render in three places. AI suggestions
  // win the de-dup priority — anything in aiSuggested is removed
  // from the FTS-suggested section AND the everything-else section.
  const aiSuggestedIds = new Set(aiSuggested.map((a) => a.id));
  const ftsSuggestedDeduped = suggested.filter((a) => !aiSuggestedIds.has(a.id));
  const ftsSuggestedIds = new Set(ftsSuggestedDeduped.map((a) => a.id));
  const otherAssets = allAssets.filter(
    (a) => !aiSuggestedIds.has(a.id) && !ftsSuggestedIds.has(a.id)
  );

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget && !attaching) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(15, 23, 42, 0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div style={{
        background: 'var(--room-card, #fff)', borderRadius: 14,
        width: 'min(720px, 100%)',
        maxHeight: '92vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(15,23,42,0.3)',
      }}>
        {/* Header */}
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--room-border, #e2e8f0)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, color: 'var(--room-text-strong, #0f172a)' }}>Add from Library</h2>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--room-text-muted, #64748b)' }}>
              Attach reusable assets from the company library to this Decision Room.
            </p>
          </div>
          <button type="button" onClick={onClose} disabled={attaching}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--room-text-muted, #64748b)', padding: 4 }}
          >×</button>
        </div>

        {/* Filters */}
        <div style={{ padding: '12px 22px', borderBottom: '1px solid var(--room-border, #e2e8f0)', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" onClick={() => setFilterType(null)}
            style={browseChipStyle(filterType === null)}>All</button>
          {ASSET_TYPES_LOCAL.map(([v, l]) => (
            <button key={v} type="button" onClick={() => setFilterType(v)}
              style={browseChipStyle(filterType === v)}>{l}</button>
          ))}
          <input
            type="text"
            placeholder="Search…"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            style={{
              padding: '6px 10px', borderRadius: 6,
              border: '1px solid #cbd5e1', fontSize: 12, fontFamily: 'inherit',
              width: 200, marginLeft: 'auto',
            }}
          />
        </div>

        {/* Body — scrollable */}
        <div style={{ flex: 1, overflow: 'auto', padding: '12px 22px' }}>
          {error && <div style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 8, background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b', fontSize: 13 }}>{error}</div>}

          {loading ? (
            <div style={{ color: 'var(--room-text-muted, #64748b)', fontSize: 13 }}>Loading library…</div>
          ) : allAssets.length === 0 ? (
            <div style={{ background: 'var(--room-surface, #f8fafc)', border: '1px dashed var(--room-border, #cbd5e1)', borderRadius: 8, padding: 24, textAlign: 'center', color: 'var(--room-text-muted, #64748b)', fontSize: 13 }}>
              {searchQ || filterType ? 'No assets match these filters.' : 'No library assets yet. Ask an admin to upload some in the Library phase.'}
            </div>
          ) : (
            <>
              {/* AI-ranked section (V3 — opt-in for cost control).
                  When no filter is active, render either:
                    - an opt-in CTA card if no cache exists yet
                    - the cached AI ranking + a "Refresh" button if it does
                  Filtering / searching collapses both — those flows
                  are "find me X", not "what should I send for THIS deal." */}
              {!filterType && !searchQ && (
                <>
                  {aiSuggested.length === 0 && !aiRunning && (
                    <div style={{
                      background: 'linear-gradient(180deg, #faf5ff 0%, #fff 100%)',
                      border: '1px dashed #c4b5fd',
                      borderRadius: 10, padding: 14, marginBottom: 16,
                      display: 'flex', alignItems: 'center', gap: 12,
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                          <SparkIcon /> AI picks for this deal
                        </div>
                        <div style={{ marginTop: 4, fontSize: 12, color: 'var(--room-text-body, #475569)', lineHeight: 1.5 }}>
                          Have the AI read this Space's discovery + stakeholders + last call summary, then rank the library with a one-line reasoning per pick.
                          {' '}
                          <span style={{ color: 'var(--room-text-subtle, #94a3b8)' }}>Cached after the first run, so re-opening this modal is instant.</span>
                        </div>
                        {aiError && (
                          <div style={{ marginTop: 6, fontSize: 12, color: '#991b1b' }}>{aiError}</div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={runAi}
                        disabled={aiRunning}
                        style={{
                          padding: '8px 14px', borderRadius: 8, border: 'none',
                          background: '#7c3aed', color: '#fff',
                          fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                          cursor: aiRunning ? 'wait' : 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Ask AI to rank
                      </button>
                    </div>
                  )}

                  {aiRunning && (
                    <div style={{
                      background: 'var(--room-accent-soft, #faf5ff)', border: '1px dashed #c4b5fd',
                      borderRadius: 10, padding: 14, marginBottom: 16,
                      fontSize: 12, color: '#6b21a8',
                    }}>
                      <div style={{ fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <SparkIcon /> AI is ranking the library…
                      </div>
                      Reading the discovery, stakeholders, and last call summary, then matching against the asset catalog. Takes ~10 seconds.
                    </div>
                  )}

                  {aiSuggested.length > 0 && (
                    <>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        marginBottom: 8,
                      }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: 0.4, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <SparkIcon /> AI picks for this deal
                        </span>
                        <span style={{ fontSize: 11, color: 'var(--room-text-subtle, #94a3b8)', marginLeft: 4 }}>
                          {aiCached && aiCachedAt ? `from ${formatRelativeTime(aiCachedAt)}` : 'just now'}
                        </span>
                        <button
                          type="button"
                          onClick={runAi}
                          disabled={aiRunning}
                          style={{
                            marginLeft: 'auto',
                            padding: '4px 10px', borderRadius: 6,
                            border: '1px solid #c4b5fd',
                            background: 'var(--room-accent-soft, #faf5ff)', color: '#7c3aed',
                            fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
                            cursor: aiRunning ? 'wait' : 'pointer',
                          }}
                          title="Re-run AI ranking with the latest Space context"
                        >
                          Refresh
                        </button>
                      </div>
                      {aiError && (
                        <div style={{ marginBottom: 8, fontSize: 12, color: '#991b1b' }}>{aiError}</div>
                      )}
                      {aiSuggested.map((a) => (
                        <BrowseRow
                          key={a.id}
                          asset={a}
                          selected={selected.has(a.id)}
                          onToggle={() => toggle(a.id)}
                          aiPick
                        />
                      ))}
                      <div style={{ height: 16 }} />
                    </>
                  )}
                </>
              )}

              {/* FTS-ranked section. Shown when there are FTS hits not
                  already covered by AI picks (e.g. AI returned 3,
                  FTS surfaces 2 more relevant ones the model missed). */}
              {!filterType && !searchQ && ftsSuggestedDeduped.length > 0 && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--room-text-body, #475569)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
                    Other matches
                  </div>
                  {ftsSuggestedDeduped.map((a) => (
                    <BrowseRow
                      key={a.id}
                      asset={a}
                      selected={selected.has(a.id)}
                      onToggle={() => toggle(a.id)}
                      highlighted
                    />
                  ))}
                  <div style={{ height: 16 }} />
                </>
              )}

              {otherAssets.length > 0 && (
                <>
                  {!filterType && !searchQ && (aiSuggested.length > 0 || ftsSuggestedDeduped.length > 0) && (
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--room-text-muted, #64748b)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
                      Everything else
                    </div>
                  )}
                  {otherAssets.map((a) => (
                    <BrowseRow
                      key={a.id}
                      asset={a}
                      selected={selected.has(a.id)}
                      onToggle={() => toggle(a.id)}
                    />
                  ))}
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 22px', borderTop: '1px solid var(--room-border, #e2e8f0)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 12, color: 'var(--room-text-muted, #64748b)' }}>
            {selected.size} selected
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button type="button" onClick={onClose} disabled={attaching}
              style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #cbd5e1', background: 'var(--room-card, #fff)', color: 'var(--room-text-strong, #1e293b)', fontSize: 13, fontFamily: 'inherit', cursor: 'pointer' }}
            >Cancel</button>
            <button type="button" onClick={attachSelected} disabled={attaching || selected.size === 0}
              style={{
                padding: '8px 16px', borderRadius: 8, border: 'none',
                background: '#7c3aed', color: '#fff', fontSize: 13, fontWeight: 600,
                fontFamily: 'inherit', cursor: (attaching || selected.size === 0) ? 'not-allowed' : 'pointer',
                opacity: (attaching || selected.size === 0) ? 0.55 : 1,
              }}
            >
              {attaching ? 'Attaching…' : `Add ${selected.size} to Space`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BrowseRow({ asset, selected, onToggle, highlighted, aiPick }) {
  // Card styling priority: aiPick > highlighted > base. AI-picked
  // rows get a soft purple gradient + a sparkle accent on the left
  // edge so the seller's eye lands on them first.
  const baseBg = aiPick
    ? 'linear-gradient(180deg, var(--room-accent-soft, #faf5ff) 0%, var(--room-card, #fff) 100%)'
    : highlighted ? 'var(--room-accent-soft, #fafaff)' : 'var(--room-card, #fff)';
  return (
    <button
      type="button"
      onClick={onToggle}
      style={{
        width: '100%', textAlign: 'left',
        background: selected ? '#f5f3ff' : baseBg,
        border: `1px solid ${selected ? '#a78bfa' : aiPick ? '#c4b5fd' : 'var(--room-border, #e2e8f0)'}`,
        borderLeft: aiPick ? '3px solid #7c3aed' : (selected ? '1px solid #a78bfa' : '1px solid var(--room-border, #e2e8f0)'),
        borderRadius: 10, padding: '10px 12px',
        marginBottom: 6, cursor: 'pointer', fontFamily: 'inherit',
        display: 'flex', alignItems: 'flex-start', gap: 10,
      }}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        onClick={(e) => e.stopPropagation()}
        style={{ width: 16, height: 16, marginTop: 2 }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--room-text-strong, #0f172a)', display: 'flex', alignItems: 'center', gap: 6 }}>
          {asset.title}
          {aiPick && asset.aiScore > 0 && (
            <span style={{
              fontSize: 10, fontWeight: 700, color: '#5b21b6',
              background: '#ede9fe', padding: '1px 6px', borderRadius: 4,
              textTransform: 'uppercase', letterSpacing: 0.3,
            }}>
              {asset.aiScore}/100
            </span>
          )}
        </div>
        {aiPick && asset.aiReasoning && (
          <div style={{
            fontSize: 12, color: '#5b21b6', marginTop: 4,
            lineHeight: 1.45, fontStyle: 'italic',
          }}>
            <SparkIcon size={11} /> {asset.aiReasoning}
          </div>
        )}
        <div style={{ fontSize: 11, color: 'var(--room-text-muted, #64748b)', marginTop: 4, display: 'flex', gap: 8 }}>
          <span>{(asset.assetType || '').replace('_', ' ')}</span>
          <span>·</span>
          <span>v{asset.currentVersion}</span>
          {asset.attachCount > 0 && <><span>·</span><span>used {asset.attachCount}x</span></>}
          {!aiPick && asset.score != null && asset.score > 0 && <><span>·</span><span>match {(asset.score).toFixed(2)}</span></>}
        </div>
      </div>
    </button>
  );
}

// Format an ISO timestamp as "5m ago" / "2h ago" / "yesterday" /
// "3d ago". Used for the "AI picks from <when>" cache annotation.
function formatRelativeTime(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '';
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  if (ms < 2 * 86_400_000) return 'yesterday';
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

// Tiny inline sparkle icon for the AI section header / reasoning prefix.
function SparkIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}>
      <path d="M12 2 L13.5 8.5 L20 10 L13.5 11.5 L12 18 L10.5 11.5 L4 10 L10.5 8.5 Z" fill="currentColor" stroke="none" />
    </svg>
  );
}

function browseChipStyle(active) {
  return {
    padding: '4px 10px', borderRadius: 999,
    border: `1px solid ${active ? '#7c3aed' : 'var(--room-border, #cbd5e1)'}`,
    background: active ? '#7c3aed' : 'var(--room-card, #fff)',
    color: active ? '#fff' : 'var(--room-text-body, #334155)',
    fontSize: 11, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
  };
}
